import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { getObstacleIcon, getArpIcon, getShieldComboIcon } from '../../utils/markers';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { addHullPolygonsToMap, getObstaclesToRender } from '../../utils/hullMapLayer';
import { T } from '../../assets/theme';
import { useSurfaceStyles } from '../../assets/SurfaceStyleContext';
import { DraggableModal, ResizeHandle } from '../../components/UI';
import { useResizableHeight } from '../../utils/useResizableHeight';
import MapMeasureTool from '../../components/MapMeasureTool';
import MapCoordinateViewer from '../../components/MapCoordinateViewer';
import { useShield, exportSurfacePenetratingCsv } from '../../utils/useShield';
import ShieldControl from '../../components/ShieldControl';
import { getProcedureForIcao } from '../../api/client';


const exportToCsv = (headers, rows, filename) => {
    const csvContent = [
        headers.join(","),
        ...rows.map(row => row.map(val => {
            const cleanVal = String(val ?? '').replace(/—/g, '-');
            return `"${cleanVal.replace(/"/g, '""')}"`;
        }).join(","))
    ].join("\n");
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

// Flags the worst-margin row per group as the controlling obstacle (mutates rows in place)
function markControllingByMargin(rows, groupKeyFn, marginFn) {
    const worst = new Map();
    rows.forEach(r => {
        const m = marginFn(r);
        if (m == null) return;
        const key = groupKeyFn(r);
        const cur = worst.get(key);
        if (!cur || m < marginFn(cur)) worst.set(key, r);
    });
    worst.forEach(r => { r.isControlling = true; });
}

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
    shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const TILE_LAYERS = {
    dark: { label: '🌙 Dark', url: 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', attribution: '© CARTO' },
    street: { label: '🗺 Street', url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', attribution: '© OSM' },
    satellite: { label: '🛰 Satellite', url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', attribution: '© Esri' },
};

const RECT_COLORS = ['#F4D03F', '#45B7D1', '#9B59B6', '#2EC98B', '#FF6B6B', '#F39C12'];
const PENETRATE_COLOR = '#FF6B6B';
const CLEAR_COLOR = '#2EC98B';

// ICAO fly-by/fly-over waypoint symbology (DEP_PROCEDURE_LEAFLET_INSTRUCTIONS.md
// / ARRIVAL_CLEARANCE_ENGINE.md convention) — never plain circles.
function waypointIcon(turnType) {
    const url = turnType === 'flyover' ? '/symbols/flyover.svg' : '/symbols/flyby.svg';
    return L.icon({ iconUrl: url, iconSize: [22, 22], iconAnchor: [11, 11] });
}
// Permanent text label flowing to the right of a waypoint symbol — a plain
// divIcon (not a Leaflet tooltip) so it never hides on mouseout and never
// intercepts clicks meant for the symbol underneath.
function waypointLabel(map, latlng, text) {
    const marker = L.marker(latlng, {
        icon: L.divIcon({
            className: '',
            html: `<div style="white-space:nowrap; padding-left:8px; font-size:10px; font-weight:700; color:#fff; text-shadow:1px 1px 2px #000,-1px -1px 2px #000,1px -1px 2px #000,-1px 1px 2px #000;">${text}</div>`,
            iconSize: [140, 14],
            iconAnchor: [0, 7],
        }),
        interactive: false,
    });
    marker.addTo(map);
    return marker;
}

// ── Procedure-segment constants ──────────────────────────────────────────────
const SEG_COLORS = {
    initial:       '#4A90E2',  // blue
    intermediate:  '#F5A623',  // amber
    final:         '#7ED321',  // green
    missed:        '#9B59B6',  // purple
    racetrack:     '#16A085',  // teal — conventional (VOR/NDB) alternate initial entry
    procedureturn: '#E91E63',  // pink — conventional alternate initial entry
    unknown:       '#888888',
};
// Primary MOC per segment (ICAO PANS-OPS). Racetrack/procedure-turn are
// alternate initial-segment maneuvers (conventional VOR/NDB procedures) —
// same 300 m primary MOC as a straight-in initial, tapering to 0 in their
// (single, non-banded) secondary area via the same _computeMOCsy used for
// initial/intermediate/final — unlike holding, which steps down band-by-band.
const MOC_M = { initial: 300, intermediate: 150, final: 75, missed: 50, racetrack: 300, procedureturn: 300 };

function segmentType(name = '') {
    const n = name.toLowerCase();
    if (/\bracetrack\b/.test(n))    return 'racetrack';
    // Matched on "procedure" alone (not "procedure turn") because the source
    // KMZs aren't always spelled consistently (e.g. FABL RWY 02's folder is
    // literally "Procedure Tunr") — no other segment folder in this naming
    // scheme uses the word "procedure", so this stays unambiguous.
    if (/\bprocedure\b/.test(n)) return 'procedureturn';
    if (/\binitial\b/.test(n))      return 'initial';
    if (/\bintermediate\b/.test(n) || /\bintermed\b/.test(n)) return 'intermediate';
    if (/\bfinal\b/.test(n))        return 'final';
    if (/\bmissed\b/.test(n))       return 'missed';
    return 'unknown';
}

// ── Planar geometry helpers for secondary-area MOC ────────────────────────
// All coordinates in latlngs are [lat, lon] pairs.

/** Convert [lat,lon] to local metres relative to a reference point. */
function _toLocal(lat, lon, refLat, refLon) {
    const mPerDegLat = 111320;
    const mPerDegLon = 111320 * Math.cos(refLat * Math.PI / 180);
    return { x: (lon - refLon) * mPerDegLon, y: (lat - refLat) * mPerDegLat };
}

/** Convert a latlngs ring [[lat,lon],...] to local {x,y} ring using refLat/refLon. */
function _ringToLocal(ring, refLat, refLon) {
    return ring.map(([lat, lon]) => _toLocal(lat, lon, refLat, refLon));
}

/**
 * Nearest point on a polygon ring boundary to point P.
 * Returns { dist, nearPt:{x,y}, outDir:{x,y} } where outDir points from nearPt toward P.
 */
function _nearestOnBoundary(P, ring2d) {
    let best = { dist: Infinity, nearPt: null, outDir: null };
    for (let i = 0; i < ring2d.length - 1; i++) {
        const A = ring2d[i], B = ring2d[i + 1];
        const dx = B.x - A.x, dy = B.y - A.y;
        const lenSq = dx * dx + dy * dy;
        if (lenSq < 1e-12) continue;
        const t = Math.max(0, Math.min(1, ((P.x - A.x) * dx + (P.y - A.y) * dy) / lenSq));
        const nearPt = { x: A.x + t * dx, y: A.y + t * dy };
        const dist = Math.hypot(P.x - nearPt.x, P.y - nearPt.y);
        if (dist < best.dist) {
            const outDx = P.x - nearPt.x, outDy = P.y - nearPt.y;
            const outLen = Math.hypot(outDx, outDy) || 1;
            best = { dist, nearPt, outDir: { x: outDx / outLen, y: outDy / outLen } };
        }
    }
    return best;
}

/**
 * Distance from point P along direction D until ray exits the polygon (first forward intersection).
 * Returns 0 if no intersection found.
 */
function _rayExitDist(P, D, ring2d) {
    const len = Math.hypot(D.x, D.y);
    if (len < 1e-12) return 0;
    const ux = D.x / len, uy = D.y / len;
    let minDist = Infinity;
    for (let i = 0; i < ring2d.length - 1; i++) {
        const A = ring2d[i], B = ring2d[i + 1];
        const edx = B.x - A.x, edy = B.y - A.y;
        const denom = ux * edy - uy * edx;
        if (Math.abs(denom) < 1e-10) continue;
        const t = ((A.x - P.x) * edy - (A.y - P.y) * edx) / denom;
        const s = ((A.x - P.x) * uy   - (A.y - P.y) * ux)  / denom;
        if (t > 1e-6 && s >= -1e-6 && s <= 1 + 1e-6 && t < minDist) minDist = t;
    }
    return minDist === Infinity ? 0 : minDist;
}

// ── Step-down fix leg matching ────────────────────────────────────────────
// A segment (Initial/Intermediate/Final) can carry more than one MOCA/OCA
// value when a step-down fix splits it into legs (see _num_list on the
// backend). The KMZ doesn't mark leg boundaries explicitly, so a leg's
// extent is derived from where a named waypoint — other than the leg's own
// start/end fix — sits along that leg's track. Everything below is scoped
// to that: project obstacles/waypoints onto the leg's track and bin them
// by which stretch of track they fall into.

/**
 * Nearest point on an OPEN polyline (not a closed ring) to point P, plus the
 * arc length from the polyline's start to that nearest point.
 * Returns { arcLength, lateralDist }.
 */
function _projectOntoPolyline(P, line2d) {
    let best = { arcLength: 0, lateralDist: Infinity };
    let cum = 0;
    for (let i = 0; i < line2d.length - 1; i++) {
        const A = line2d[i], B = line2d[i + 1];
        const dx = B.x - A.x, dy = B.y - A.y;
        const segLen = Math.hypot(dx, dy);
        if (segLen < 1e-9) continue;
        const t = Math.max(0, Math.min(1, ((P.x - A.x) * dx + (P.y - A.y) * dy) / (segLen * segLen)));
        const nearX = A.x + t * dx, nearY = A.y + t * dy;
        const dist = Math.hypot(P.x - nearX, P.y - nearY);
        const arc = cum + t * segLen;
        if (dist < best.lateralDist) best = { arcLength: arc, lateralDist: dist };
        cum += segLen;
    }
    return best;
}

/**
 * A "leg" node is a folder that carries its own track (LineString features
 * directly on it), e.g. "UP2T1-UP2F1" between the IF and FAF. Concatenate
 * its LineString pieces in order (by trailing "-#N" suffix, if present) into
 * one continuous [lat,lon] polyline.
 */
function _legTrackLatLngs(legNode) {
    const lines = (legNode?.features || [])
        .filter(f => f.geom === 'LineString' && f.coords?.length)
        .map(f => {
            const m = /#(\d+)\s*$/.exec(f.name || '');
            return { order: m ? parseInt(m[1], 10) : 0, coords: f.coords };
        })
        .sort((a, b) => a.order - b.order);
    const pts = [];
    for (const l of lines) for (const c of l.coords) pts.push(c);
    return pts;
}

/** Parse "STARTFIX-ENDFIX" leg-node names into the two fix names, if it matches. */
function _legEndpointNames(legNode) {
    const m = /^([A-Z0-9]+)-([A-Z0-9]+)$/i.exec((legNode?.name || '').trim());
    return m ? { start: m[1].toUpperCase(), end: m[2].toUpperCase() } : { start: null, end: null };
}

/** Collect every named Point (waypoint) feature anywhere in the layer tree. */
function _collectAllWaypoints(node) {
    const results = [];
    (node.features || []).forEach(f => {
        if (f.geom === 'Point' && f.coords) {
            results.push({ name: (f.name || '').replace(/^WP\s+/i, '').trim().toUpperCase(), latlng: f.coords });
        }
    });
    for (const child of (node.children || [])) results.push(..._collectAllWaypoints(child));
    return results;
}

/**
 * Work out the along-track distance boundaries that split a leg into
 * `mocaList.length` pieces, one per step-down value, using named waypoints
 * that land on the leg's track between its start and end fix.
 *
 * Returns { boundaries, totalLen, refLat, refLon, matched: true } when
 * exactly the right number of interior waypoints was found (i.e. legs
 * count = mocaList.length), or { matched: false } otherwise — callers
 * should fall back to the single most conservative value in that case.
 */
function _buildStepdownBins(legNode, mocaList, allWaypoints) {
    if (!legNode || mocaList.length <= 1) return { matched: false };
    const track = _legTrackLatLngs(legNode);
    if (track.length < 2) return { matched: false };

    const [refLat, refLon] = track[0];
    const trackLocal = track.map(([lat, lon]) => _toLocal(lat, lon, refLat, refLon));
    const totalLen = _projectOntoPolyline(trackLocal[trackLocal.length - 1], trackLocal).arcLength;
    if (totalLen < 1) return { matched: false };

    const { start, end } = _legEndpointNames(legNode);
    const LATERAL_TOL_M = 150;   // waypoint must sit within this of the track to count
    const EDGE_TOL_M     = 50;   // ignore projections effectively at either endpoint

    const candidates = [];
    for (const wp of allWaypoints) {
        if (!wp.name || wp.name === start || wp.name === end) continue;
        const P = _toLocal(wp.latlng[0], wp.latlng[1], refLat, refLon);
        const { arcLength, lateralDist } = _projectOntoPolyline(P, trackLocal);
        if (lateralDist <= LATERAL_TOL_M && arcLength > EDGE_TOL_M && arcLength < totalLen - EDGE_TOL_M) {
            candidates.push(arcLength);
        }
    }
    candidates.sort((a, b) => a - b);

    if (candidates.length !== mocaList.length - 1) return { matched: false };
    return { matched: true, boundaries: [0, ...candidates, totalLen], trackLocal, refLat, refLon };
}

/** Resolve which MOCA/OCA value applies to an obstacle given a leg's stepdown bins. */
function _mocaForObstacle(bins, mocaList, obsLat, obsLon) {
    if (!bins.matched) return Math.max(...mocaList); // conservative fallback — see _buildStepdownBins
    const P = _toLocal(obsLat, obsLon, bins.refLat, bins.refLon);
    const { arcLength } = _projectOntoPolyline(P, bins.trackLocal);
    let idx = bins.boundaries.length - 2; // last bin by default
    for (let i = 0; i < bins.boundaries.length - 1; i++) {
        if (arcLength >= bins.boundaries[i] && arcLength < bins.boundaries[i + 1]) { idx = i; break; }
    }
    return mocaList[idx];
}

/**
 * Compute MOCsy for an obstacle inside a secondary protection area.
 *   Y  = distance from obstacle to nearest primary-area boundary
 *   Ws = secondary width at that cross-section (primary boundary → outer secondary edge)
 *   MOCsy = max(0, (1 - Y/Ws) * MOCp)
 * Returns MOCp if geometry cannot be resolved.
 */
function _computeMOCsy(obsLat, obsLon, primaryRing, secondaryRing, MOCp) {
    if (!primaryRing || !secondaryRing) return MOCp;
    const P = _toLocal(obsLat, obsLon, obsLat, obsLon); // {x:0, y:0}
    const primLocal = _ringToLocal(primaryRing, obsLat, obsLon);
    const secLocal  = _ringToLocal(secondaryRing, obsLat, obsLon);

    const { dist: Y, nearPt, outDir } = _nearestOnBoundary(P, primLocal);
    if (!nearPt || !outDir) return MOCp;

    // Ws = distance from the nearest primary-boundary point outward to secondary boundary
    const Ws = _rayExitDist(nearPt, outDir, secLocal);
    if (Ws <= 0 || Y >= Ws) return 0; // obstacle too far out — MOCsy rounds to 0

    return Math.max(0, (1 - Y / Ws) * MOCp);
}

/**
 * Walk the layer tree collecting polygon features.
 * `state` is a shared mutable object: { seenFinal, seenMissed }
 * — prevents duplicate Final/Missed segments from multiple IAF branches.
 */
function collectPolygons(node, ancestorSeg = 'unknown', ancestorLeg = null) {
    const nodeSeg = segmentType(node.name || '');
    const seg = nodeSeg !== 'unknown' ? nodeSeg : ancestorSeg;

    // Skip TAA nodes — handled separately by collectTAA
    if ((node.name || '').toLowerCase().includes('taa')) return [];
    // Skip Holding nodes — handled separately by collectHolding
    if ((node.name || '').toLowerCase().includes('hold')) return [];

    // A "leg" node carries this stretch's track directly (LineString features
    // attached to it, e.g. "UP2T1-UP2F1") — remembered so polygons nested
    // under it (its "Protection Areas" child) can be matched to step-down
    // fixes along that specific track. See _buildStepdownBins.
    const hasTrack = (node.features || []).some(f => f.geom === 'LineString');
    const leg = hasTrack ? node : ancestorLeg;

    const results = [];
    (node.features || []).forEach((f, fi) => {
        // Tag each polygon as primary or secondary from its feature name
        const fname = (f.name || '').toLowerCase();
        // "VSS Area" polygons are sometimes drawn alongside Final's real
        // Primary/Secondary Area for visualization — they belong to the
        // separate Visual Segment Surface tab (computed independently
        // there, not from this KMZ), not the NPA segment/MOC engine, so
        // must not be swept in here as a spurious extra "primary" area.
        if (fname.includes('vss')) return;
        const areaType = fname.includes('secondary') ? 'secondary' : 'primary';
        if (f.geom === 'Polygon' && f.coords) {
            const ring = f.coords[0] || f.coords;
            results.push({
                segType: seg, name: f.name || node.name || 'Area',
                latlngs: ring, nodeName: node.name || '',
                nodeId: node.id,
                fKey: featureKey(node.id, fi),
                areaType,
                legNode: leg,
            });
        }
    });
    for (const child of (node.children || [])) results.push(...collectPolygons(child, seg, leg));
    return results;
}

/**
 * Collect line/track features from the layer tree.
 * Shares the same `state` object to deduplicate Final/Missed from multiple IAF branches.
 */
function collectLines(node, ancestorSeg = 'unknown') {
    const nodeSeg = segmentType(node.name || '');
    const seg = nodeSeg !== 'unknown' ? nodeSeg : ancestorSeg;

    const results = [];
    (node.features || []).forEach((f, fi) => {
        if (f.geom === 'LineString' && f.coords) {
            results.push({
                segType: seg, name: f.name || node.name || '',
                latlngs: f.coords, nodeId: node.id,
                fKey: featureKey(node.id, fi),
            });
        }
    });
    for (const child of (node.children || [])) results.push(...collectLines(child, seg));
    return results;
}

/**
 * Collect waypoint (Point) features from the layer tree.
 */
function collectPoints(node, ancestorSeg = 'unknown') {
    const nodeSeg = segmentType(node.name || '');
    const seg = nodeSeg !== 'unknown' ? nodeSeg : ancestorSeg;

    const results = [];
    (node.features || []).forEach((f, fi) => {
        if (f.geom === 'Point' && f.coords) {
            results.push({
                segType: seg, name: f.name || node.name || '',
                latlng: f.coords, nodeId: node.id,
                fKey: featureKey(node.id, fi),
            });
        }
    });
    for (const child of (node.children || [])) results.push(...collectPoints(child, seg));
    return results;
}

/**
 * Point-in-polygon test (ray casting) for Leaflet LatLng coords.
 * polylatlngs: [[lat,lon],...]
 */
function pointInPoly(lat, lon, poly) {
    let inside = false;
    for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
        const [yi, xi] = poly[i];
        const [yj, xj] = poly[j];
        if (((yi > lat) !== (yj > lat)) && (lon < (xj - xi) * (lat - yi) / (yj - yi) + xi)) {
            inside = !inside;
        }
    }
    return inside;
}

/**
 * Walk the layer tree collecting TAA sector polygons for penetration assessment.
 *
 * After reparent_taa(), structure is:
 *   Approach → TAA → <any subfolders> → Polygon features
 *
 * altitude resolution priority:
 *   1. procTaaAlts lookup by sector name (si/lb/rb from DB)
 *   2. Feature-level f.taa_alt_ft (KML Z coordinate, parsed by kmz_preparse)
 *   3. Parent-inherited altitude
 *   4. null (row still collected; shows "NO TAA ALT")
 *
 * @param {object} node         - Layer tree node
 * @param {boolean} inTaaContext - True once we've entered the TAA node
 * @param {number|null} inheritedAlt - Altitude inherited from parent
 * @param {object} procTaaAlts  - { si_ft, lb_ft, rb_ft } from DB
 */
function collectTAA(node, inTaaContext = false, inheritedAlt = null, procTaaAlts = {}, resolvedType = null) {
    const results = [];
    const name = node.name || '';
    const nameLower = name.toLowerCase();

    const entersTaa = nameLower === 'taa' || nameLower.startsWith('taa');
    const nowInTaa  = inTaaContext || entersTaa;

    // Detect sector type from node name — propagates to all descendant polygons
    let myType = resolvedType;
    if (!myType) {
        if (nameLower.includes('straight') || nameLower === 'si' || nameLower === 'straight_in')
            myType = 'SI';
        else if (nameLower.includes('left') || nameLower === 'lb' || nameLower === 'left_base')
            myType = 'LB';
        else if (nameLower.includes('right') || nameLower === 'rb' || nameLower === 'right_base')
            myType = 'RB';
    }

    // Node-level altitude from parser
    let nodeAlt = node.taa_alt_ft ?? null;
    if (nodeAlt === null && entersTaa && nameLower !== 'taa') {
        const m = name.match(/(\d{3,5})\s*(?:ft|feet|msl)?/i);
        if (m) nodeAlt = parseInt(m[1], 10);
    }

    // Lookup altitude from DB-stored proc TAA altitudes by sector type. Each
    // quadrant (SI/LB/RB) can have more than one physically distinct sector
    // (e.g. FAKN RWY05's RIGHT_BASE: Sector #1 + Sector #2), each with its own
    // MOCA — stored as an ordered array (spreadsheet cell "7300;8200") keyed
    // by quadrant. A node named "Sector #<n>" selects index n-1; anywhere else
    // under the quadrant falls back to the first value.
    const { si_ft, lb_ft, rb_ft } = procTaaAlts;
    const altRaw = myType === 'SI' ? si_ft : myType === 'LB' ? lb_ft : myType === 'RB' ? rb_ft : null;
    const altList = Array.isArray(altRaw) ? altRaw : (altRaw != null ? [altRaw] : []);
    const sectorMatch = name.match(/sector\s*#?\s*(\d+)/i);
    const sectorIndex = sectorMatch ? parseInt(sectorMatch[1], 10) - 1 : null;
    const sectorDbAlt = altList.length === 0 ? null
        : (sectorIndex !== null && altList[sectorIndex] !== undefined) ? altList[sectorIndex]
        : altList[0];

    const effectiveAlt = sectorDbAlt ?? nodeAlt ?? inheritedAlt;

    if (nowInTaa) {
        (node.features || []).forEach((f, fi) => {
            if (f.geom !== 'Polygon' || !f.coords) return;
            const altFt = sectorDbAlt ?? f.taa_alt_ft ?? effectiveAlt ?? null;
            const ring  = f.coords[0] || f.coords;
            // fKey includes resolvedType so that three different "Sector #1" nodes
            // (one under RIGHT_BASE, STRAIGHT_IN, LEFT_BASE) stay distinct
            const uniqueFKey = `${featureKey(node.id, fi)}_${myType || 'UNK'}`;
            results.push({
                sectorName:   name,
                featureName:  f.name || name,
                sectorType:   myType,       // 'SI' | 'LB' | 'RB' | null
                taa_alt_ft:   altFt,
                latlngs:      ring,
                nodeId:       node.id,
                fKey:         uniqueFKey,
            });
        });
    }

    for (const child of (node.children || [])) {
        results.push(...collectTAA(child, nowInTaa, effectiveAlt, procTaaAlts, myType));
    }
    return results;
}


// ── Holding-pattern area MOC bands ──────────────────────────────────────────
// The KMZ marks the holding protection area two different ways:
//   - A "Primary Area" Polygon feature (the actual protected primary area).
//   - Six closed-loop "Secondary Area-#1".."#6" LineString rings, drawn as
//     distance markers inside the (separate, unlabelled-by-band) "Secondary
//     Area" polygon. #1 coincides with the Primary Area boundary; #2..#6 are
//     concentric rings each ~1 NM further out. There is no per-band Polygon —
//     the rings are the only source of the 1/2/3/4/5 NM boundaries, so they
//     are treated as closed polygon rings for point-in-ring testing (their
//     coords already form a closed loop: first point === last point).
// MOC steps down band-by-band per ICAO PANS-OPS holding criteria (unlike the
// segment secondary areas' linear MOCsy taper — see _computeMOCsy):
//   Primary Area                     → 300 m
//   Primary boundary (#1) → #2       → 300 m
//   #2 → #3                          → 150 m
//   #3 → #4                          → 120 m
//   #4 → #5                          →  90 m
//   #5 → #6                          →  60 m
//   Beyond #6                        → outside protection, not assessed
// #1 is redundant with Primary Area (same boundary) and is intentionally not
// in this map, so obstacles inside Primary Area are matched by the polygon
// itself rather than by ring #1.
const HOLDING_BAND_MOC_M = { primary: 300, 2: 300, 3: 150, 4: 120, 5: 90, 6: 60 };

// TAA MOC = 300 m (ICAO PANS-OPS). Module-level (not component-scoped) so it's
// always initialized before any code path — e.g. an early useMemo — that
// might call computeTaaPenetrations() before the component body reaches its
// own local declarations further down.
const TAA_MOC_M = 300;
const TAA_MOC_FT = TAA_MOC_M / 0.3048;

/** Identify a holding feature's band (primary polygon, or secondary-#N ring) from its name. */
function _holdingBandFor(name = '') {
    const n = name.toLowerCase();
    if (n.includes('primary')) return { band: 'primary', order: 0, moc: HOLDING_BAND_MOC_M.primary };
    const m = /#\s*(\d+)/.exec(n);
    if (n.includes('secondary') && m) {
        const num = parseInt(m[1], 10);
        if (HOLDING_BAND_MOC_M[num] !== undefined) {
            return { band: `sec${num}`, order: num - 1, moc: HOLDING_BAND_MOC_M[num] };
        }
    }
    return null;
}

/**
 * Walk the layer tree collecting holding-pattern primary-area polygons and
 * secondary-band boundary rings, grouped by the deepest enclosing "HOLDING"
 * node name so multiple holdings on one procedure (e.g. one per IAF) stay
 * distinct. Mirrors collectTAA's enter-context/inherit pattern.
 */
function collectHolding(node, inHoldContext = false, holdingName = null) {
    const results = [];
    const name = node.name || '';
    const nameLower = name.toLowerCase();
    const entersHold = nameLower.includes('hold');
    const nowInHold = inHoldContext || entersHold;
    // Prefer the deepest matching node's name (e.g. "Holding NC1N3" over its
    // parent "Holding") so the results table shows which holding fix this is.
    const myHoldingName = entersHold ? name : holdingName;

    if (nowInHold) {
        (node.features || []).forEach((f, fi) => {
            if ((f.geom !== 'Polygon' && f.geom !== 'LineString') || !f.coords) return;
            const bandInfo = _holdingBandFor(f.name || node.name || '');
            if (!bandInfo) return;
            // Polygon coords are [[ring]]; these LineString rings are already
            // a flat, closed [lat,lon] loop.
            const ring = f.geom === 'Polygon' ? (f.coords[0] || f.coords) : f.coords;
            results.push({
                holdingName: myHoldingName || name,
                featureName:  f.name || name,
                band:         bandInfo.band,
                order:        bandInfo.order,
                moc:          bandInfo.moc,
                latlngs:      ring,
                nodeId:       node.id,
                fKey:         `${featureKey(node.id, fi)}_${bandInfo.band}`,
            });
        });
    }

    for (const child of (node.children || [])) {
        results.push(...collectHolding(child, nowInHold, myHoldingName));
    }
    return results;
}

const featureKey = (nodeId, fi) => `${nodeId}_f${fi}`;

function getAllNodeIds(node) {
    const ids = node.id ? [node.id] : [];
    // Include per-feature keys so they can be toggled individually
    if (node.id) {
        (node.features || []).forEach((_, fi) => ids.push(featureKey(node.id, fi)));
    }
    for (const c of (node.children || [])) ids.push(...getAllNodeIds(c));
    return ids;
}

function initTreeVis(node) {
    const vis = {};
    for (const id of getAllNodeIds(node)) vis[id] = true;
    return vis;
}

/**
 * Cascade a boolean value to a node and all its descendants in the vis map.
 */
function cascadeVis(vis, node, value) {
    const next = { ...vis };
    for (const id of getAllNodeIds(node)) next[id] = value;
    return next;
}

/**
 * Return true/false/null (indeterminate) for a node given its children.
 * true  = all descendants checked
 * false = all descendants unchecked
 * null  = mixed (indeterminate)
 */
function nodeCheckedState(vis, node) {
    const ids = getAllNodeIds(node);
    const allTrue  = ids.every(id => vis[id] !== false);
    const allFalse = ids.every(id => vis[id] === false);
    if (allTrue)  return true;
    if (allFalse) return false;
    return null; // indeterminate
}

// ── Recursive tree node component ───────────────────────────────────────────
/**
 * Recursive tree node for procedure GeoJSON layers.
 * state: { seenFinal, seenMissed } - shared across the recursion to deduplicate IAF branches.
 */
function ProcedureTreeNode({ node, vis, onChange, depth = 0, ancestorSeg = 'unknown' }) {
    const nodeSeg = segmentType(node.name || '');
    const currentSeg = nodeSeg !== 'unknown' ? nodeSeg : ancestorSeg;
    // Only polygon features shown as leaves (lines/tracks toggled by parent checkbox)
    const polyFeatures = (node.features || []).filter(f => f.geom === 'Polygon');
    const hasPolyFeatures = polyFeatures.length > 0;

    // No global deduplication in tree (reverted to show full KML structure)
    const children = node.children || [];
    const hasChildren = children.length > 0;
    const isExpandable = hasChildren || hasPolyFeatures;
    const [expanded, setExpanded] = React.useState(false);

    const checkedState = nodeCheckedState(vis, node);
    const isChecked = checkedState === true;
    const isIndeterminate = checkedState === null;
    const seg = segmentType(node.name || '');
    const col = seg !== 'unknown' ? SEG_COLORS[seg] : depth === 0 ? '#e0e0e0' : '#aaa';

    const cbRef = React.useRef(null);
    React.useEffect(() => {
        if (cbRef.current) cbRef.current.indeterminate = isIndeterminate;
    }, [isIndeterminate]);

    const indent = depth * 12;

    return (
        <div>
            {/* Node row */}
            <div style={{
                display: 'flex', alignItems: 'center', gap: 4,
                paddingLeft: indent, paddingTop: 2, paddingBottom: 2,
            }}>
                {isExpandable ? (
                    <span
                        onClick={() => setExpanded(e => !e)}
                        style={{ cursor: 'pointer', fontSize: 9, color: '#666', width: 10, flexShrink: 0, userSelect: 'none' }}
                    >
                        {expanded ? '▼' : '▶'}
                    </span>
                ) : (
                    <span style={{ width: 10, flexShrink: 0 }} />
                )}

                <input
                    ref={cbRef}
                    type="checkbox"
                    checked={isChecked}
                    onChange={e => onChange(node, e.target.checked)}
                    style={{ accentColor: col, cursor: 'pointer', flexShrink: 0, margin: 0 }}
                />

                {seg !== 'unknown' && depth > 0 && (
                    <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: 2, background: col, flexShrink: 0 }} />
                )}

                <span
                    onClick={isExpandable ? () => setExpanded(e => !e) : undefined}
                    style={{
                        fontSize: depth === 0 ? 11 : 10,
                        fontWeight: depth === 0 ? 700 : depth === 1 ? 600 : 400,
                        color: isChecked ? (col !== '#aaa' ? col : T.white) : '#666',
                        cursor: isExpandable ? 'pointer' : 'default',
                        flexShrink: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}
                    title={node.name}
                >
                    {node.name}
                </span>
            </div>

            {/* Expanded: child nodes (deduplicated for final/missed) */}
            {expanded && hasChildren && children.map(child => (
                <ProcedureTreeNode
                    key={child.id || child.name}
                    node={child} vis={vis} onChange={onChange} depth={depth + 1}
                    ancestorSeg={currentSeg}
                />
            ))}

            {/* Expanded: polygon features as leaves (Primary / Secondary Area) */}
            {expanded && hasPolyFeatures && polyFeatures.map((f, fi) => {
                const fk = featureKey(node.id, fi);
                const fChecked = vis[fk] !== false;
                // Determine display name: "Primary Area" / "Secondary Area" from feature name
                const fname = f.name || (f.geom === 'LineString' ? 'Track' : 'Area');
                const isPrimary = fname.toLowerCase().includes('primary');
                const isSecondary = fname.toLowerCase().includes('secondary');
                const fcol = isPrimary ? '#2EC98B' : isSecondary ? '#F4A300' : '#aaa';
                const fIndent = (depth + 1) * 12;
                return (
                    <div key={fi} style={{
                        display: 'flex', alignItems: 'center', gap: 4,
                        paddingLeft: fIndent, paddingTop: 1, paddingBottom: 1,
                    }}>
                        <span style={{ width: 10, flexShrink: 0 }} />
                        <input
                            type="checkbox"
                            checked={fChecked}
                            onChange={e => {
                                // toggle individual feature key
                                onChange({ id: fk, features: [], children: [] }, e.target.checked);
                            }}
                            style={{ accentColor: fcol, cursor: 'pointer', flexShrink: 0, margin: 0 }}
                        />
                        <span style={{
                            display: 'inline-block', width: 5, height: 5,
                            borderRadius: 1,
                            background: f.geom === 'LineString' ? 'transparent' : fcol,
                            border: f.geom === 'LineString' ? `2px solid ${fcol}` : 'none',
                            flexShrink: 0,
                        }} />
                        <span style={{
                            fontSize: 9, color: fChecked ? fcol : '#555',
                            flexShrink: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}
                            title={fname}
                        >
                            {fname}
                        </span>
                    </div>
                );
            })}
        </div>
    );
}

function getRectColor(icao, allIcaos) {
    const idx = allIcaos.indexOf(icao);
    return RECT_COLORS[idx % RECT_COLORS.length];
}

function StatusBadge({ penetrates }) {
    if (penetrates === null || penetrates === undefined)
        return <span style={{ display:'inline-block', padding:'2px 8px', borderRadius:10, fontSize:10, fontWeight:700, color:'#fff', background:'#44444422', border:'1px solid #66666655' }}>NO MINIMA</span>;
    const c = penetrates ? T.danger : T.safe;
    return <span style={{ display:'inline-block', padding:'2px 8px', borderRadius:10, fontSize:10, fontWeight:700, color:'#fff', background:c+'33', border:`1px solid ${c}55` }}>{penetrates ? '⚠ PENETRATES' : '✓ CLEAR'}</span>;
}

export default function NpaPage({ project, arps, obstacles, loading, npaState, setNpaState, showObstacles = true, showObsLabels, showArpLabels, onObstacleContextMenu, onMapReady, onAerodromeContextMenu }) {
    const mapRef = useRef(null);
    const mapInst = useRef(null);
    // Step 2 reference set — this project's own obstacles that actually
    // penetrate an NPA area/segment/TAA sector, not just its tallest point
    // overall. Reuses computeProcPenetrations/computeTaaPenetrations (plain
    // function declarations further down, hoisted within this component) —
    // the exact same pure functions the marker-render effect uses per-obstacle.
    const npaResultsForShield = npaState?.npaResults || {};
    const procDataForShield = npaState?.procData || {};
    const penetratingObstacleIds = React.useMemo(() => {
        const ids = [];
        const icaos = Object.keys(npaResultsForShield);
        (obstacles || []).forEach(o => {
            let pen = false;
            icaos.forEach(icao => {
                const results = npaResultsForShield[icao]?.results || [];
                if (results.some(r => r.obstacle_id === o.id && r.penetrates === true)) pen = true;
                (procDataForShield[icao] || []).forEach(proc => {
                    if (computeProcPenetrations(proc, [o]).some(r => r.penetrates)) pen = true;
                    if (computeTaaPenetrations(proc, [o]).some(r => r.penetrates)) pen = true;
                });
            });
            if (pen) ids.push(o.id);
        });
        return ids;
    }, [npaResultsForShield, procDataForShield, obstacles]);
    const { shieldOn, toggleShield, shieldData, shieldLoading, shieldError, projectMaxTotalElev, projectPenetratingMaxTotalElev, radiusKm, setRadiusKm, exportCsv, shieldLayerRef } = useShield(mapInst, obstacles, penetratingObstacleIds);
    const tileRef = useRef(null);
    const layersRef = useRef([]);  // { key: string, layer: L.Layer }[]
    const { getStyle } = useSurfaceStyles();
    const segColors = useMemo(() => ({
        initial: getStyle('npa.initial'),
        intermediate: getStyle('npa.intermediate'),
        final: getStyle('npa.final'),
        missed: getStyle('npa.missed'),
        unknown: { color: '#888888', opacity: 0.12 },
    }), [getStyle]);

    const [mapMode, setMapMode] = useState('street');
    const [mapState, setMapState] = useState(null);
    const [npaAerodromes, setNpaAerodromes] = useState([]);
    const [assessing, setAssessing] = useState({});        // { [icao]: bool }
    const [capturedNpa, setCapturedNpa] = useState({});    // { [icao]: bool } – aerodrome-level, ephemeral is OK
    const [capturing, setCapturing] = useState(false);
    const [activeIcao, setActiveIcao] = useState(null);    // which aerodrome's results table is shown
    const [filterDir, setFilterDir] = useState('ALL');
    const [expandedMinima, setExpandedMinima] = useState({});  // { [icao]: bool }
    const [exportingAixm, setExportingAixm] = useState(false);
    const [resultTab, setResultTab] = useState('npa');
    const [resultsCollapsed, setResultsCollapsed] = useState(false);
    const [resultsHeight, startResultsResize] = useResizableHeight(280, { min: 120, max: 700 });
    const [resultsPoppedOut, setResultsPoppedOut] = useState(false);

    useEffect(() => {
        if (mapInst.current) {
            setTimeout(() => { mapInst.current?.invalidateSize(); }, 200);
        }
    }, [resultsCollapsed, resultsPoppedOut]);

    // ── Bind to hoisted state ────────────────────────────────────────────────
    const npaResults = npaState?.npaResults || {};
    const setNpaResults = (val) => setNpaState(prev => ({ ...prev, npaResults: typeof val === 'function' ? val(prev.npaResults || {}) : val }));

    const visNpa = npaState?.visNpa || {};
    const setVisNpa = (val) => setNpaState(prev => ({ ...prev, visNpa: typeof val === 'function' ? val(prev.visNpa || {}) : val }));

    // procData, procTreeVis, capturedNpaRwy are hoisted so they persist across tab navigation
    const procData = npaState?.procData || {};
    const setProcData = (val) => setNpaState(prev => ({ ...prev, procData: typeof val === 'function' ? val(prev.procData || {}) : val }));

    const procTreeVis = npaState?.procTreeVis || {};
    const setProcTreeVis = (val) => setNpaState(prev => ({ ...prev, procTreeVis: typeof val === 'function' ? val(prev.procTreeVis || {}) : val }));

    const capturedNpaRwy = npaState?.capturedNpaRwy || {};
    const setCapturedNpaRwy = (val) => setNpaState(prev => ({ ...prev, capturedNpaRwy: typeof val === 'function' ? val(prev.capturedNpaRwy || {}) : val }));

    const obsWithCoords = React.useMemo(
        () => obstacles.filter(o => o.latitude && o.longitude),
        [obstacles]
    );
    const assessedIcaos = React.useMemo(
        () => Object.keys(npaResults),
        [npaResults]
    );

    // Load NPA-capable aerodromes
    useEffect(() => {
        const fetch = async () => {
            try {
                const { getNpaAerodromes } = await import('../../api/client');
                const res = await getNpaAerodromes();
                setNpaAerodromes(res.data || []);
            } catch (err) { console.error('NPA aerodromes error:', err); }
        };
        fetch();
    }, []);

    const validArps = React.useMemo(
        () => arps.filter(a => npaAerodromes.includes(a.icao?.toUpperCase())),
        [arps, npaAerodromes]
    );

    // ── Map init ──────────────────────────────────────────────────────────────
    useEffect(() => {
        if (!mapRef.current || mapInst.current) return;
        mapInst.current = L.map(mapRef.current, { center: [-28.5, 25.0], zoom: 6, zoomDelta: 0.7, zoomSnap: 0.7 });
        tileRef.current = L.tileLayer(TILE_LAYERS[mapMode].url, { attribution: TILE_LAYERS[mapMode].attribution, maxZoom: 24, maxNativeZoom: 19 }).addTo(mapInst.current);
        const ro = new ResizeObserver(() => mapInst.current?.invalidateSize());
        ro.observe(mapRef.current);
        setMapState(mapInst.current);
        onMapReady?.(mapInst.current);
        return () => { ro.disconnect(); mapInst.current?.remove(); mapInst.current = null; tileRef.current = null; setMapState(null); onMapReady?.(null); };
    }, []);

    // Swap tile layer
    useEffect(() => {
        if (!mapInst.current) return;
        if (tileRef.current) mapInst.current.removeLayer(tileRef.current);
        const layer = TILE_LAYERS[mapMode];
        tileRef.current = L.tileLayer(layer.url, { attribution: layer.attribution, maxZoom: 24, maxNativeZoom: 19 }).addTo(mapInst.current);
    }, [mapMode]);

    // ── Draw/update map layers whenever results or visibility changes ──────────
    useEffect(() => {
        if (!mapInst.current || loading) return;

        // Remove all previous NPA layers
        layersRef.current.forEach(({ layer }) => mapInst.current.removeLayer(layer));
        layersRef.current = [];

        const bounds = [];

        // ARP markers — hide if aerodrome is assessed but toggled off
        validArps.forEach(arp => {
            if (!arp.lat || !arp.lon) return;
            const icao = arp.icao?.toUpperCase();
            const isAssessed = !!npaResults[icao];
            const allVisible = !isAssessed || visNpa[`${icao}_ALL`] !== false;
            if (!allVisible) return;  // hide when master toggle is off
            const ll = [arp.lat, arp.lon];
            const color = getRectColor(icao, assessedIcaos);
            const m = L.marker(ll, { icon: getArpIcon(L, arp, showArpLabels) })
                .bindPopup(`<b>✈ ${arp.icao}</b><br/>${arp.name}`);
            if (onAerodromeContextMenu) {
                m.on('contextmenu', e => {
                    e.originalEvent.preventDefault();
                    onAerodromeContextMenu(arp, e.originalEvent.clientX, e.originalEvent.clientY);
                });
            }
            m.addTo(mapInst.current);
            layersRef.current.push({ key: `arp_${icao}`, layer: m });
        });

        // NPA rectangles per aerodrome
        assessedIcaos.forEach(icao => {
            const data = npaResults[icao];
            if (!data) return;
            const rectColor = getRectColor(icao, assessedIcaos);

            // Threshold markers
            (data.thresholds || []).forEach(thr => {
                if (!thr.lat || !thr.lon) return;
                const key = `${icao}_thr_${thr.designator}`;
                const ll = [parseFloat(thr.lat), parseFloat(thr.lon)];
                const isVisible = visNpa[`${icao}_ALL`] !== false;
                if (!isVisible) return;
                const m = L.circleMarker(ll, { radius: 3, color: rectColor, fillColor: rectColor, fillOpacity: 0.9, weight: 2 })
                    .bindPopup(`<b>THR ${thr.designator}</b><br/>Elev: ${thr.elev_m ?? '—'} m`);
                m.addTo(mapInst.current);
                layersRef.current.push({ key, layer: m });
                bounds.push(ll);
            });

            // Rectangle polygons per runway pair
            (data.polygons?.features || []).forEach(f => {
                const rwyPair = f.properties?.rwy_pair || 'unknown';
                const visKey = `${icao}_${rwyPair}`;
                const allKey = `${icao}_ALL`;
                // Default visible if not explicitly set to false
                const isVisible = visNpa[allKey] !== false && visNpa[visKey] !== false;
                if (!isVisible) return;

                const coords = f.geometry?.coordinates?.[0];
                if (!coords) return;
                const latlngs = coords.map(([lon, lat]) => [lat, lon]);

                const poly = L.polygon(latlngs, {
                    color: rectColor, weight: 2,
                    fillColor: rectColor, fillOpacity: 0.45,
                    dashArray: '8,4'
                });
                const { total_length_m, width_m, oca_a_ft, oca_b_ft } = f.properties;
                poly.bindTooltip(
                    `<b>${icao} – NPA Area RWY ${rwyPair}</b><br/>` +
                    `Width: ${(width_m / 1852).toFixed(1)} NM &nbsp; Length: ${(total_length_m / 1852).toFixed(1)} NM<br/>` +
                    `OCA: ${oca_a_ft ?? '–'} ft / ${oca_b_ft ?? '–'} ft`,
                    { sticky: true }
                );
                poly.addTo(mapInst.current);
                layersRef.current.push({ key: `${icao}_rect_${rwyPair}`, layer: poly });
                latlngs.forEach(ll => bounds.push(ll));
            });
        });

        // Obstacle dots — coloured by worst penetration status across all assessed aerodromes
        getObstaclesToRender(obsWithCoords, project?.id, showObstacles).forEach(o => {
            const ll = [o.latitude, o.longitude];
            bounds.push(ll);
            let isPenetrating = false;
            let isInArea = false;
            let isAssessed = assessedIcaos.length > 0;
            let maxPen = -Infinity;
            let foundAny = false;
            let surfaceStr = '—';

            assessedIcaos.forEach(icao => {
                const results = npaResults[icao]?.results || [];
                const obsRows = results.filter(r => r.obstacle_id === o.id);
                if (obsRows.length) { isInArea = true; }
                if (obsRows.some(r => r.penetrates === true)) { isPenetrating = true; }

                obsRows.forEach(r => {
                    foundAny = true;
                    const elev = parseFloat(r.obstacle_elev_m ?? 0);
                    const vtol = parseFloat(r.v_tolerance_m ?? r.v_tolerance ?? 0);
                    const effective = parseFloat(r.effective_m ?? (elev + vtol + 75));
                    const oca = r.oca_m ?? null;
                    if (oca !== null) {
                        const margin = oca - effective;
                        const pen = -margin;
                        if (pen > maxPen) {
                            maxPen = pen;
                            surfaceStr = `NPA Area (RWY ${r.approach_direction})`;
                        }
                    }
                });
            });

            // Also check procedure-based segment penetration
            assessedIcaos.forEach(icao => {
                const procs = procData[icao] || [];
                procs.forEach(proc => {
                    const procPen = computeProcPenetrations(proc, [o]);
                    if (procPen.some(r => r.penetrates)) { isPenetrating = true; isInArea = true; }
                    procPen.forEach(r => {
                        foundAny = true;
                        if (r.margin_m !== null) {
                            const pen = -r.margin_m;
                            if (pen > maxPen) {
                                maxPen = pen;
                                surfaceStr = `Segment: ${r.segment_name} (Proc: ${r.procedure_name})`;
                            }
                        }
                    });

                    const taaPen = computeTaaPenetrations(proc, [o]);
                    if (taaPen.some(r => r.penetrates)) { isPenetrating = true; isInArea = true; }
                    taaPen.forEach(r => {
                        foundAny = true;
                        if (r.margin_ft !== null) {
                            const pen = -r.margin_ft * 0.3048;
                            if (pen > maxPen) {
                                maxPen = pen;
                                surfaceStr = `TAA Sector: ${r.sector_name} (Proc: ${r.procedure_name})`;
                            }
                        }
                    });

                    const holdPen = computeHoldingPenetrations(proc, [o]);
                    if (holdPen.some(r => r.penetrates)) { isPenetrating = true; isInArea = true; }
                    holdPen.forEach(r => {
                        foundAny = true;
                        if (r.margin_ft !== null) {
                            const pen = -r.margin_ft * 0.3048;
                            if (pen > maxPen) {
                                maxPen = pen;
                                surfaceStr = `Holding: ${r.band_label} (Proc: ${r.procedure_name})`;
                            }
                        }
                    });
                });
            });

            let penetrationStr = '—';
            if (foundAny) {
                if (maxPen > 0) {
                    penetrationStr = `${maxPen.toFixed(2)} m`;
                } else {
                    penetrationStr = `${maxPen.toFixed(2)} m (Clear)`;
                }
            }

            const color = isPenetrating ? PENETRATE_COLOR : isInArea ? CLEAR_COLOR : '#888';
            const icon = getObstacleIcon(L, o, isAssessed ? (isPenetrating ? 'penetrated' : 'clear') : undefined, showObsLabels);
            const m = L.marker(ll, { icon })
                .bindPopup(`<b>${o.name}</b><br/>ID: ${o.id}<br/>ELEVATION : ${o.elevation_m ?? '—'} m<br/>SURFACE: ${surfaceStr}<br/>PENETRATION: ${penetrationStr}`);
            if (onObstacleContextMenu) {
                m.on('contextmenu', e => {
                    e.originalEvent.preventDefault();
                    onObstacleContextMenu(o, e.originalEvent.clientX, e.originalEvent.clientY);
                });
            }
            m.addTo(mapInst.current);
            layersRef.current.push({ key: `obs_${o.id}`, layer: m });
        });
        addHullPolygonsToMap(mapInst.current, obsWithCoords, project?.id, layersRef, (l, i) => ({ key: `hull_poly_${i}`, layer: l }));

        // ── Procedure KMZ layers ───────────────────────────────────────────────
        assessedIcaos.forEach(icao => {
            const procs = procData[icao];
            if (!procs) return;
            procs.forEach((proc, pi) => {
                const allSegments = collectPolygons(proc.json_layers || {});
                const allLines = collectLines(proc.json_layers || {});
                const allPoints = collectPoints(proc.json_layers || {});

                // ── Draw procedure KMZ layers ──────────────────────────
                allSegments.forEach((seg, si) => {
                    const vis = procTreeVis[icao];
                    // Hide if the feature itself OR its parent node is toggled off
                    if (vis && (vis[seg.fKey] === false || vis[seg.nodeId] === false)) return;
                    const segStyle = segColors[seg.segType] || segColors.unknown;
                    const poly = L.polygon(seg.latlngs, {
                        color: segStyle.color, weight: 1.5,
                        fillColor: segStyle.color, fillOpacity: segStyle.opacity,
                        dashArray: '4,4',
                    }).bindTooltip(
                        `<b>${proc.procedure_name}</b><br/>${seg.nodeName || seg.name}<br/>${seg.segType.toUpperCase()} segment`,
                        { sticky: true }
                    );
                    poly.addTo(mapInst.current);
                    layersRef.current.push({ key: `proc_${icao}_${pi}_poly_${si}`, layer: poly });
                });

                // ── Draw procedure tracks ───────────────────────────────
                // Nominal track legs (waypoint-to-waypoint, e.g. "LE1N3-LE1T1",
                // and the "Holding <fix>-#N" racetrack pieces) are drawn black,
                // matching standard PANS-OPS chart convention — the flown path
                // in black, distinct from the colored/dashed protection-area
                // boundaries. "Secondary Area-#N" rings are also LineString
                // (collectLines sweeps the whole tree) but are holding MOC-band
                // boundaries, not track — they already get their own colored
                // render below (collectHolding), so they keep their segment
                // color here instead of a redundant black outline on top.
                allLines.forEach((ln, li) => {
                    const vis = procTreeVis[icao];
                    if (vis && (vis[ln.fKey] === false || vis[ln.nodeId] === false)) return;
                    const isAreaBoundary = /area/i.test(ln.name || '');
                    const lnColor = isAreaBoundary ? (segColors[ln.segType] || segColors.unknown).color : '#000000';
                    const pl = L.polyline(ln.latlngs, { color: lnColor, weight: 2, opacity: 0.8 })
                        .bindTooltip(`${ln.name}`, { sticky: true });
                    pl.addTo(mapInst.current);
                    layersRef.current.push({ key: `proc_${icao}_${pi}_line_${li}`, layer: pl });
                });

                // ── Draw procedure waypoints ────────────────────────────
                allPoints.forEach((pt, wi) => {
                    const vis = procTreeVis[icao];
                    if (vis && (vis[pt.fKey] === false || vis[pt.nodeId] === false)) return;
                    const cleanName = (pt.name || '').replace(/^WP\s+/i, '');
                    const wp = L.marker(pt.latlng, { icon: waypointIcon(pt.turn_type) })
                        .bindPopup(`<b>${cleanName || 'Waypoint'}</b>`);
                    wp.addTo(mapInst.current);
                    layersRef.current.push({ key: `proc_${icao}_${pi}_wp_${wi}`, layer: wp });
                    layersRef.current.push({ key: `proc_${icao}_${pi}_wplabel_${wi}`, layer: waypointLabel(mapInst.current, pt.latlng, cleanName || 'Waypoint') });
                });

                // ── Draw TAA sector polygons (cyan dashed) ────────────────
                const procTaaAlts = {
                    si_ft: proc.taa_si_ft ?? null,
                    lb_ft: proc.taa_lb_ft ?? null,
                    rb_ft: proc.taa_rb_ft ?? null,
                };
                collectTAA(proc.json_layers || {}, false, null, procTaaAlts).forEach((sector, ti) => {
                    const vis = procTreeVis[icao];
                    if (vis && (vis[sector.fKey] === false || vis[sector.nodeId] === false)) return;
                    const taaColor = '#06b6d4';
                    const poly = L.polygon(sector.latlngs, {
                        color: taaColor, weight: 2,
                        fillColor: taaColor, fillOpacity: 0.08,
                        dashArray: '4 3',
                    }).bindTooltip(
                        `<b>TAA — ${sector.sectorName}</b><br/>${sector.featureName}` +
                        (sector.taa_alt_ft ? `<br/>Lower Limit: <b>${sector.taa_alt_ft} ft</b>` : ''),
                        { sticky: true }
                    );
                    poly.addTo(mapInst.current);
                    layersRef.current.push({ key: `proc_${icao}_${pi}_taa_${ti}`, layer: poly });
                });

                // ── Draw Holding primary/secondary area polygons (banded) ──
                const holdingRingColors = { primary: '#2EC98B', sec2: '#2EC98B', sec3: '#F4A300', sec4: '#F4A300', sec5: '#e67e22', sec6: '#e67e22' };
                collectHolding(proc.json_layers || {}).forEach((ring, hi) => {
                    const vis = procTreeVis[icao];
                    if (vis && (vis[ring.fKey] === false || vis[ring.nodeId] === false)) return;
                    const hCol = holdingRingColors[ring.band] || '#e67e22';
                    const poly = L.polygon(ring.latlngs, {
                        color: hCol, weight: 1.5,
                        fillColor: hCol, fillOpacity: 0.05,
                        dashArray: '3 3',
                    }).bindTooltip(
                        `<b>Holding — ${ring.holdingName}</b><br/>${ring.featureName}<br/>MOC: <b>${ring.moc} m</b>` +
                        (proc.hold_ft ? `<br/>Hold Alt: <b>${proc.hold_ft} ft</b>` : ''),
                        { sticky: true }
                    );
                    poly.addTo(mapInst.current);
                    layersRef.current.push({ key: `proc_${icao}_${pi}_hold_${hi}`, layer: poly });
                });
            });
        });

        // Never auto-fit — user controls the zoom. fitBounds is called
        // explicitly in runAssessment when new data loads.
    }, [validArps, obstacles, loading, npaResults, procData, procTreeVis, visNpa, showObstacles, showObsLabels, showArpLabels, assessedIcaos, onObstacleContextMenu, onAerodromeContextMenu, segColors]); // eslint-disable-line

    // ── Procedure penetration engine ─────────────────────────────────────────
    function computeProcPenetrations(proc, obsArray) {
        const FT_TO_M = 0.3048;
        const M_TO_FT = 1 / FT_TO_M;

        // Each of these is an ordered list — normally one value, but a
        // step-down fix gives a segment more than one (see _num_list on the
        // backend; for Final the last value is the OCA).
        const asList = (v) => (Array.isArray(v) && v.length ? v : [0]);
        const segMOCA_LIST = {
            initial:       asList(proc.initial_moca_ft),
            intermediate:  asList(proc.intermediate_moca_ft),
            final:         asList(proc.final_oca_ft),
            missed:        asList(proc.final_oca_ft),  // missed uses final OCA
            racetrack:     asList(proc.racetrack_moca_ft),
            procedureturn: asList(proc.procedure_turn_moca_ft),
        };

        const allPolys = collectPolygons(proc.json_layers || {});
        const allWaypoints = _collectAllWaypoints(proc.json_layers || {});

        // Build a lookup: nodeId → primary ring (for secondary MOC calculation)
        const primaryByNode = {};
        for (const seg of allPolys) {
            if (seg.areaType === 'primary') primaryByNode[seg.nodeId] = seg.latlngs;
        }

        // Step-down bins are per-leg (a segment can have several parallel IAF
        // branches, each needing its own along-track split), computed once
        // per leg and reused for every obstacle that falls in it.
        const binsCache = new Map();
        function binsFor(seg, mocaList) {
            const key = seg.legNode?.id ?? `${seg.segType}:none`;
            if (!binsCache.has(key)) {
                binsCache.set(key, _buildStepdownBins(seg.legNode, mocaList, allWaypoints));
            }
            return binsCache.get(key);
        }

        // Collect raw results (may have duplicates for same obs+segment)
        const raw = [];
        for (const obs of obsArray) {
            const obsElevM = parseFloat(obs.elevation_m) || 0;
            const vAcc     = parseFloat(obs.v_tolerance) || 0;
            for (const seg of allPolys) {
                if (!pointInPoly(obs.latitude, obs.longitude, seg.latlngs)) continue;

                // MOCp = primary MOC for segment. VNAV Final uses 49m; others standard.
                let MOCp = MOC_M[seg.segType] ?? 75;
                if (seg.segType === 'final' && proc.procedure_type?.includes('VNAV')) {
                    MOCp = 49;
                }

                // Compute effective MOC depending on area type
                let moc, areaLabel;
                if (seg.areaType === 'secondary') {
                    const primRing = primaryByNode[seg.nodeId] || null;
                    moc = _computeMOCsy(obs.latitude, obs.longitude, primRing, seg.latlngs, MOCp);
                    areaLabel = 'secondary';
                } else {
                    moc = MOCp;
                    areaLabel = 'primary';
                }

                // Resolve the MOCA/OCA that applies to THIS obstacle: a flat
                // scalar when the segment has one value, or the step-down
                // value for whichever leg-stretch the obstacle projects into.
                const mocaList = segMOCA_LIST[seg.segType] || [0];
                let moca_ft, stepdown;
                if (mocaList.length <= 1) {
                    moca_ft = mocaList[0] || 0;
                    stepdown = null;
                } else {
                    const bins = binsFor(seg, mocaList);
                    moca_ft = _mocaForObstacle(bins, mocaList, obs.latitude, obs.longitude);
                    stepdown = bins.matched ? 'matched' : 'conservative-fallback';
                }
                const moca_m = moca_ft * FT_TO_M;

                const totalElev_m  = obsElevM + vAcc + moc;
                const totalElev_ft = totalElev_m * M_TO_FT;
                const penetrates   = moca_ft > 0 ? totalElev_ft > moca_ft : null;
                raw.push({
                    obstacle_id:    obs.id,
                    obstacle_name:  obs.name,
                    procedure_id:   proc.id,
                    procedure_name: proc.procedure_name,
                    runway:         proc.runway,
                    segment:        seg.segType,
                    segment_name:   seg.nodeName || seg.name,
                    area_type:      areaLabel,
                    obs_elev_m:     +obsElevM.toFixed(1),
                    v_accuracy_m:   +vAcc.toFixed(1),
                    moc_m:          +moc.toFixed(2),
                    total_elev_m:   +totalElev_m.toFixed(1),
                    total_elev_ft:  +totalElev_ft.toFixed(1),
                    moca_ft:        moca_ft > 0 ? +moca_ft.toFixed(0) : null,
                    moca_m:         moca_m  > 0 ? +moca_m.toFixed(1)  : null,
                    margin_ft:      moca_ft > 0 ? +(moca_ft - totalElev_ft).toFixed(0) : null,
                    margin_m:       moca_m  > 0 ? +(moca_m  - totalElev_m).toFixed(1)  : null,
                    penetrates,
                    moca_stepdown:  stepdown,
                });
            }
        }

        // ── Deduplicate by (obstacle_id, segment, area_type, name): keep worst-case ──
        const seen = new Map(); // key: `${obs_id}_${segType}_${areaType}_${name}`
        for (const r of raw) {
            const key = `${r.obstacle_id}_${r.segment}_${r.area_type}_${r.segment_name}`;
            const existing = seen.get(key);
            if (!existing) { seen.set(key, r); continue; }
            const rank = (p) => p === true ? 2 : p === null ? 1 : 0;
            if (rank(r.penetrates) > rank(existing.penetrates)) seen.set(key, r);
        }

        return [...seen.values()];
    }

    // ── TAA penetration engine ────────────────────────────────────────────────
    function computeTaaPenetrations(proc, obsArray) {
        const M_TO_FT = 1 / 0.3048;
        // Use DB-stored TAA altitudes; fall back to KML Z coordinate
        const procTaaAlts = {
            si_ft: proc.taa_si_ft ?? null,
            lb_ft: proc.taa_lb_ft ?? null,
            rb_ft: proc.taa_rb_ft ?? null,
        };
        const taaSectors = collectTAA(proc.json_layers || {}, false, null, procTaaAlts);
        if (taaSectors.length === 0) return [];

        const raw = [];
        for (const obs of obsArray) {
            const obsElevM = parseFloat(obs.elevation_m) || 0;
            const vAcc     = parseFloat(obs.v_tolerance) || 0;

            for (const sector of taaSectors) {
                if (!pointInPoly(obs.latitude, obs.longitude, sector.latlngs)) continue;

                const totalElev_m  = obsElevM + vAcc + TAA_MOC_M;
                const totalElev_ft = totalElev_m * M_TO_FT;
                const taa_ft       = sector.taa_alt_ft;
                const penetrates   = taa_ft > 0 ? totalElev_ft > taa_ft : null;

                // sectorType is already resolved by collectTAA from ancestor node names
                // (RIGHT_BASE → 'RB', STRAIGHT_IN → 'SI', LEFT_BASE → 'LB')
                const type = sector.sectorType || null;
                // Build display label — avoid "Sector #1 - Sector #1" duplicate
                const secLabel = (sector.featureName && sector.featureName !== sector.sectorName)
                    ? `${sector.sectorName} - ${sector.featureName}`
                    : sector.sectorName;
                const sector_name = type ? `${type} - ${secLabel}` : secLabel;

                raw.push({
                    obstacle_id:    obs.id,
                    obstacle_name:  obs.name,
                    rwy:            proc.runway || '—',
                    sector_name,
                    node_id:        sector.nodeId,
                    f_key:          sector.fKey,    // unique polygon identity — used for dedup
                    obs_elev_m:     +obsElevM.toFixed(1),
                    v_accuracy_m:   +vAcc.toFixed(1),
                    moc_m:          TAA_MOC_M,
                    total_elev_m:   +totalElev_m.toFixed(1),
                    total_elev_ft:  +totalElev_ft.toFixed(0),
                    taa_alt_ft:     taa_ft || null,
                    margin_ft:      taa_ft > 0 ? +(taa_ft - totalElev_ft).toFixed(0) : null,
                    penetrates,
                });
            }
        }

        // Deduplicate: keep worst-case per (obstacle, polygon).
        // Key uses f_key (unique per polygon in the KMZ tree) so sectors that share
        // the same altitude (SI=LB=RB is common) are NEVER collapsed together.
        const seen = new Map();
        for (const r of raw) {
            const key = `${r.obstacle_id}_${r.f_key}`;
            const ex  = seen.get(key);
            if (!ex) { seen.set(key, r); continue; }
            const rank = (p) => p === true ? 2 : p === null ? 1 : 0;
            if (rank(r.penetrates) > rank(ex.penetrates)) seen.set(key, r);
        }
        return [...seen.values()];
    }

    // ── Holding-area penetration engine ────────────────────────────────────────
    // MOC steps down per band (see HOLDING_BAND_MOC_M / collectHolding) rather
    // than the linear MOCsy taper used for segment secondary areas. Reference
    // altitude is the published minimum holding altitude (proc.hold_ft).
    function computeHoldingPenetrations(proc, obsArray) {
        const M_TO_FT = 1 / 0.3048;
        const holdFt = proc.hold_ft ?? null;
        const rings = collectHolding(proc.json_layers || {});
        if (rings.length === 0) return [];

        // Group rings by holding pattern, innermost band first — rings are
        // nested/cumulative so the first (smallest) containing ring wins.
        const byHolding = new Map();
        for (const r of rings) {
            const key = r.holdingName || 'HOLDING';
            if (!byHolding.has(key)) byHolding.set(key, []);
            byHolding.get(key).push(r);
        }
        for (const list of byHolding.values()) list.sort((a, b) => a.order - b.order);

        const raw = [];
        for (const obs of obsArray) {
            const obsElevM = parseFloat(obs.elevation_m) || 0;
            const vAcc     = parseFloat(obs.v_tolerance) || 0;

            for (const [holdingName, list] of byHolding) {
                const hit = list.find(r => pointInPoly(obs.latitude, obs.longitude, r.latlngs));
                if (!hit) continue;

                const totalElev_m  = obsElevM + vAcc + hit.moc;
                const totalElev_ft = totalElev_m * M_TO_FT;
                const penetrates   = holdFt > 0 ? totalElev_ft > holdFt : null;

                raw.push({
                    obstacle_id:    obs.id,
                    obstacle_name:  obs.name,
                    procedure_id:   proc.id,
                    procedure_name: proc.procedure_name,
                    rwy:            proc.runway || '—',
                    holding_name:   holdingName,
                    band:           hit.band,
                    band_label:     hit.band === 'primary' ? 'Primary' : `Secondary #${hit.band.replace('sec', '')}`,
                    node_id:        hit.nodeId,
                    f_key:          hit.fKey,
                    obs_elev_m:     +obsElevM.toFixed(1),
                    v_accuracy_m:   +vAcc.toFixed(1),
                    moc_m:          hit.moc,
                    total_elev_m:   +totalElev_m.toFixed(1),
                    total_elev_ft:  +totalElev_ft.toFixed(0),
                    hold_ft:        holdFt || null,
                    margin_ft:      holdFt > 0 ? +(holdFt - totalElev_ft).toFixed(0) : null,
                    penetrates,
                });
            }
        }

        // Deduplicate: keep worst-case per (obstacle, polygon).
        const seen = new Map();
        for (const r of raw) {
            const key = `${r.obstacle_id}_${r.f_key}`;
            const ex  = seen.get(key);
            if (!ex) { seen.set(key, r); continue; }
            const rank = (p) => p === true ? 2 : p === null ? 1 : 0;
            if (rank(r.penetrates) > rank(ex.penetrates)) seen.set(key, r);
        }
        return [...seen.values()];
    }

    // ── Missed Approach segment shielding — real, not flat ────────────────────
    // Same reasoning as OLS/VSS: the missed-approach segment's protection
    // area is sloped/positional (MOC tapers from primary to secondary), so
    // "shields" has to mean "already penetrates THIS segment, at its own
    // position, by at least as much as the project's own worst obstacle
    // does" — not "is taller". Entirely client-side: reuses
    // computeProcPenetrations, the exact function already used for real
    // project obstacles, against each SWOT/SOF candidate — no extra network
    // call needed since procedure geometry (json_layers) is already loaded,
    // unlike OLS/OLS ADG/VSS which need a backend round-trip.
    const missedApproachImpacts = useMemo(() => {
        const out = {}; // `${icao}_${runway}` -> worst (most negative) margin_m
        Object.entries(procData).forEach(([icao, procs]) => {
            (procs || []).forEach(proc => {
                computeProcPenetrations(proc, obsWithCoords).forEach(r => {
                    if (r.segment !== 'missed' || !r.penetrates || r.margin_m == null) return;
                    const key = `${icao}_${r.runway}`;
                    if (!(key in out) || r.margin_m < out[key]) out[key] = r.margin_m;
                });
            });
        });
        return out;
    }, [procData, obsWithCoords]);
    const hasMissedApproachImpact = Object.keys(missedApproachImpacts).length > 0;

    // Candidate pseudo-id -> [{ icao, runway, margin, projectMargin }, ...],
    // plus a companion "was it even inside the missed-approach segment at
    // all" map — computeProcPenetrations returns margin_m: null for a point
    // outside the segment, which is a different situation from "inside but
    // not penetrating enough to shield", so the popup can say which one.
    const [missedApproachShieldVerdicts, missedApproachInsideSegment, missedApproachRealPenetration] = useMemo(() => {
        const out = new Map();
        const inside = new Map();
        const realPen = new Map();
        if (!shieldOn || !shieldData || !hasMissedApproachImpact) return [out, inside, realPen];
        const candidates = [
            ...(shieldData.swot || []).map(r => ({ ...r, kind: 'SWOT' })),
            ...(shieldData.sof || []).map(r => ({ ...r, kind: 'SOF' })),
        ].filter(r => r.lat != null && r.lon != null);
        if (!candidates.length) return [out, inside, realPen];

        const pseudoObstacles = candidates.map(r => ({
            id: `shield_${r.kind}_${r.ogc_fid}`,
            name: r.kind === 'SWOT' ? (r.caa_id || r.type) : (r.site_name || r.type),
            latitude: r.lat, longitude: r.lon,
            elevation_m: parseFloat(r.elev_m) || 0,
            v_tolerance: parseFloat(r.v_tolerance_m) || 0,
        }));

        Object.entries(procData).forEach(([icao, procs]) => {
            (procs || []).forEach(proc => {
                computeProcPenetrations(proc, pseudoObstacles).forEach(r => {
                    if (r.segment !== 'missed' || r.margin_m == null) return;
                    inside.set(r.obstacle_id, true);
                    // Real penetration at this candidate's own position,
                    // independent of raw elevation or of beating the
                    // project's worst margin — see olsShieldRealPenetration
                    // in OLSPage.jsx.
                    if (r.penetrates) {
                        const penList = realPen.get(r.obstacle_id) || [];
                        penList.push({ icao, runway: r.runway, margin: r.margin_m });
                        realPen.set(r.obstacle_id, penList);
                    }
                    const key = `${icao}_${r.runway}`;
                    const projectMargin = missedApproachImpacts[key];
                    if (projectMargin == null) return;
                    if (r.margin_m <= projectMargin) {
                        const list = out.get(r.obstacle_id) || [];
                        list.push({ icao, runway: r.runway, margin: r.margin_m, projectMargin });
                        out.set(r.obstacle_id, list);
                    }
                });
            });
        });
        return [out, inside, realPen];
    }, [shieldOn, shieldData, hasMissedApproachImpact, missedApproachImpacts, procData]);

    // Enrich each shield marker's own popup (from useShield) with the real
    // Missed Approach verdict(s), same pattern as OLSPage.jsx. Also
    // recolours the marker yellow for a genuine real-surface penetration
    // that wasn't already red (raw-elevation shield).
    const comboShieldLayerRef = useRef(null); // half-red/half-yellow halo: red (raw-elevation) AND genuinely penetrates a real surface
    useEffect(() => {
        const group = shieldLayerRef.current;
        if (comboShieldLayerRef.current) { mapInst.current?.removeLayer(comboShieldLayerRef.current); comboShieldLayerRef.current = null; }
        if (!group || !hasMissedApproachImpact) return;
        const comboGroup = L.featureGroup();
        group.eachLayer(layer => {
            if (!layer._shieldKey || !layer._basePopupHtml) return;
            const verdicts = missedApproachShieldVerdicts.get(layer._shieldKey);
            const insideAnySurface = missedApproachInsideSegment.get(layer._shieldKey) || false;
            const realPenList = missedApproachRealPenetration.get(layer._shieldKey) || [];
            const realPenetration = realPenList.length > 0;
            const realPenNames = [...new Set(realPenList.map(v2 => `Missed Approach ${v2.icao} RWY ${v2.runway}`))];
            const outsideLine = `<br/><span style="color:#8a94a6">Outside surface?: ${insideAnySurface ? 'No' : 'Yes'}</span>`;
            const penetratesLine = realPenetration
                ? `<br/><span style="color:#2D9CDB">Penetrates real surface (regardless of height)?: Yes — ${realPenNames.join(', ')}</span>`
                : `<br/><span style="color:#8a94a6">Penetrates real surface (regardless of height)?: No</span>`;
            const surfacesLine = (verdicts && verdicts.length)
                ? verdicts.map(v =>
                    `<br/><b style="color:#FFB020">Missed Approach:</b> ${v.icao} RWY ${v.runway} ` +
                    `(this obstacle's margin ${v.margin.toFixed(1)} m vs project's worst ${v.projectMargin.toFixed(1)} m)`
                  ).join('')
                : `<br/><span style="color:#8a94a6">Missed Approach: No</span>`;
            layer.setPopupContent(layer._basePopupHtml + outsideLine + penetratesLine + surfacesLine);
            if (realPenetration && !layer._shieldsRaw) {
                layer.setStyle({ color: '#FFE14D', fillColor: '#FFE14D', weight: 2, radius: 7, fillOpacity: 0.85 });
            } else if (realPenetration && layer._shieldsRaw) {
                const halo = L.marker(layer.getLatLng(), { icon: getShieldComboIcon(L), interactive: false });
                comboGroup.addLayer(halo);
            }
        });
        if (comboGroup.getLayers().length > 0) {
            comboShieldLayerRef.current = comboGroup;
            comboGroup.addTo(mapInst.current);
        }
    }, [shieldLayerRef, missedApproachShieldVerdicts, missedApproachInsideSegment, missedApproachRealPenetration, hasMissedApproachImpact, shieldData, shieldOn]);

    // Distinct marker layer for every candidate confirmed to truly shield the
    // Missed Approach segment somewhere.
    const missedShieldLayerRef = useRef(null);
    useEffect(() => {
        if (!mapInst.current) return;
        if (missedShieldLayerRef.current) { mapInst.current.removeLayer(missedShieldLayerRef.current); missedShieldLayerRef.current = null; }
        if (!shieldOn || missedApproachShieldVerdicts.size === 0) return;

        const group = L.featureGroup();
        const byId = new Map();
        [...(shieldData?.swot || []).map(r => ({ ...r, kind: 'SWOT' })),
         ...(shieldData?.sof || []).map(r => ({ ...r, kind: 'SOF' }))].forEach(r => {
            byId.set(`shield_${r.kind}_${r.ogc_fid}`, r);
        });

        missedApproachShieldVerdicts.forEach((verdicts, obsId) => {
            const r = byId.get(obsId);
            if (!r || r.lat == null || r.lon == null) return;
            const marker = L.circleMarker([r.lat, r.lon], {
                radius: 10, color: '#FFB020', weight: 3, fillColor: '#FFB020', fillOpacity: 0.35,
            });
            const label = r.kind === 'SWOT' ? (r.caa_id || r.type) : (r.site_name || r.type);
            const lines = verdicts.map(v =>
                `<b>${v.icao} RWY ${v.runway}</b> — margin <b>${v.margin.toFixed(1)} m</b> (project worst: ${v.projectMargin.toFixed(1)} m)`
            ).join('<br/>');
            marker.bindPopup(
                `<b>🔄 TRUE MISSED APPROACH SHIELD</b><br/>${r.kind}: ${label}<br/>ID: ${r.caa_id || '—'} (row ${r.ogc_fid})<br/>${lines}<br/>` +
                `<span style="color:#FFB020">Already penetrates as much or more — the project is not a new impact there.</span>`
            );
            group.addLayer(marker);
        });

        if (group.getLayers().length > 0) {
            missedShieldLayerRef.current = group;
            group.addTo(mapInst.current);
        }
    }, [shieldOn, missedApproachShieldVerdicts, shieldData]);

    // ── Assess an aerodrome (merges into existing results) ────────────────────
    const runAssessment = useCallback(async (icao) => {
        setAssessing(prev => ({ ...prev, [icao]: true }));
        try {
            const { assessNpa } = await import('../../api/client');
            const obsPayload = obsWithCoords.map(o => ({
                id: o.id, name: o.name,
                latitude: o.latitude, longitude: o.longitude,
                elevation_m: o.elevation_m || 0,
                v_tolerance: o.v_tolerance || 0,
                h_tolerance: o.h_tolerance || 0,
            }));
            const res = await assessNpa(icao, obsPayload);
            // Merge into existing results (don't wipe other aerodromes)
            setNpaResults(prev => ({ ...prev, [icao]: res.data }));
            // Default all runway pairs visible for this icao
            const pairs = (res.data.polygons?.features || []).map(f => f.properties?.rwy_pair).filter(Boolean);
            setVisNpa(prev => {
                const next = { ...prev };
                if (next[`${icao}_ALL`] === undefined) next[`${icao}_ALL`] = true;
                pairs.forEach(p => { if (next[`${icao}_${p}`] === undefined) next[`${icao}_${p}`] = true; });
                return next;
            });

            // ── Load procedure layers for this ICAO ──────────────────────────
            try {
                const procRes = await getProcedureForIcao(icao);
                const rows = procRes.data;
                setProcData(prev => ({ ...prev, [icao]: rows }));
                // Init tree visibility: every node id -> true
                setProcTreeVis(prev => {
                    const next = { ...prev };
                    const merged = { ...(prev[icao] || {}) };
                    rows.forEach(proc => {
                        const nodeVis = initTreeVis(proc.json_layers || {});
                        Object.assign(merged, nodeVis);
                    });
                    next[icao] = merged;
                    return next;
                });

                // ── Compute procedure penetration results and store in npaResults ──
                const padRwy = (r) => {
                    // Normalize: strip 'RWY' prefix and spaces, then zero-pad numeric part
                    const s = String(r || '').replace(/^RWY\s*/i, '').trim();
                    const num = s.replace(/\D+$/, '');  // numeric part
                    const suf = s.slice(num.length);    // suffix (L/R/C)
                    return num ? String(parseInt(num, 10)).padStart(2, '0') + suf.toUpperCase() : s.padStart(2, '0');
                };
                const procResults = {}; // { [rwy]: { lnav, vnav, taa, hold, proc_names } }
                rows.forEach(proc => {
                    const rwy = padRwy(proc.runway);
                    if (!procResults[rwy]) procResults[rwy] = { lnav: [], vnav: [], taa: [], hold: [], proc_names: { lnav: [], vnav: [] } };
                    const isVnav = proc.procedure_type?.toUpperCase().includes('VNAV');
                    const subtype = isVnav ? 'vnav' : 'lnav';
                    // Store procedure name for sub-header
                    if (!procResults[rwy].proc_names[subtype].includes(proc.procedure_name)) {
                        procResults[rwy].proc_names[subtype].push(proc.procedure_name);
                    }
                    // Procedure segment penetrations
                    const pen = computeProcPenetrations(proc, obsWithCoords);
                    procResults[rwy][subtype].push(...pen);
                    // TAA penetrations
                    const taaPen = computeTaaPenetrations(proc, obsWithCoords);
                    procResults[rwy].taa.push(...taaPen);
                    // Holding-area penetrations
                    const holdPen = computeHoldingPenetrations(proc, obsWithCoords);
                    procResults[rwy].hold.push(...holdPen);
                });
                setNpaResults(prev => ({
                    ...prev,
                    [icao]: { ...(prev[icao] || {}), procedure_results: procResults }
                }));
                // Persist TAA/holding results so the report generator can include them
                const allTaa = Object.entries(procResults).flatMap(([rwy, r]) =>
                    (r.taa || []).map(t => ({ ...t, rwy }))
                );
                const allHold = Object.entries(procResults).flatMap(([rwy, r]) =>
                    (r.hold || []).map(h => ({ ...h, rwy }))
                );
                if (project?.id) {
                    import('../../api/client').then(m => {
                        m.saveNpaTaaResults(icao, allTaa, project.id).catch(() => {});
                        m.saveNpaHoldResults(icao, allHold, project.id).catch(() => {});
                    });
                }
            } catch {
                setProcData(prev => ({ ...prev, [icao]: [] }));
            }

            // Fit map to the new aerodrome once (only on first assessment)
            const arp = validArps.find(a => a.icao?.toUpperCase() === icao);
            if (arp?.lat && arp?.lon && mapInst.current) {
                const thrs = res.data.thresholds || [];
                const pts = [[arp.lat, arp.lon], ...thrs.map(t => [parseFloat(t.lat), parseFloat(t.lon)]).filter(([a, b]) => !isNaN(a) && !isNaN(b))];
                mapInst.current.fitBounds(L.latLngBounds(pts), { padding: [80, 80], maxZoom: 13 });
            }
            setActiveIcao(icao);
            setFilterDir('ALL');
        } catch (err) { console.error('NPA assessment error:', err); }
        finally { setAssessing(prev => ({ ...prev, [icao]: false })); }
    }, [obsWithCoords]); // eslint-disable-line

    async function handleExportAixm() {
        if (!project?.id) return;
        setExportingAixm(true);
        try {
            const API_BASE = import.meta.env.VITE_API_URL || '/api';
            const resp = await fetch(`${API_BASE}/projects/${project.id}/export/aixm?surfaces=procedures,taa`);
            if (!resp.ok) {
                const body = await resp.json().catch(() => ({}));
                throw new Error(body.detail || `Export failed (${resp.status})`);
            }
            const blob = await resp.blob();
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            // Server resolves the real aerodrome(s) from obstacle proximity and
            // builds the filename itself — read it back from the response
            // rather than reconstructing one from project.aerodrome (which can
            // be stale/left at its default 'FAOR', the exact bug fixed for the
            // OLS export earlier).
            const cd = resp.headers.get('content-disposition') || '';
            const nameMatch = cd.match(/filename="?([^";]+)"?/);
            link.download = nameMatch ? nameMatch[1] : `project_${project.id}_export_PROCEDURES_AIXM51.xml`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Failed to export AIXM:', err);
            alert(err.message || 'Failed to export AIXM. Please check your network connection and try again.');
        } finally {
            setExportingAixm(false);
        }
    }

    // ── Screenshot helpers ────────────────────────────────────────────────────
    // Obstacle icons are SVG-based divIcons. Browsers report naturalWidth=0 for
    // SVG <img> elements without explicit dimensions in the SVG root, so the
    // `img.naturalWidth` check fails silently. We fetch each icon's src as a
    // blob and load it with explicit target dimensions instead — matching the
    // same approach already used for map tiles.
    const drawMarkersOnCanvas = useCallback(async (ctx, container, containerRect) => {
        const markerEls = [...container.querySelectorAll('.leaflet-marker-icon')];
        await Promise.all(markerEls.map(async marker => {
            const img = marker.querySelector('img');
            if (!img || !img.src) return;
            const mRect = marker.getBoundingClientRect();
            const x = Math.round(mRect.left - containerRect.left);
            const y = Math.round(mRect.top - containerRect.top);
            const w = Math.round(mRect.width) || 20;
            const h = Math.round(mRect.height) || 20;
            try {
                const resp = await fetch(img.src);
                if (!resp.ok) return;
                const blob = await resp.blob();
                const url = URL.createObjectURL(blob);
                await new Promise(resolve => {
                    const im = new Image();
                    im.onload = () => { ctx.drawImage(im, x, y, w, h); URL.revokeObjectURL(url); resolve(); };
                    im.onerror = () => { URL.revokeObjectURL(url); resolve(); };
                    im.src = url;
                });
            } catch {}
        }));
    }, []);

    // ── Screenshot capture ────────────────────────────────────────────────────
    const captureNpaScreenshot = useCallback(async (icao) => {
        if (!mapRef.current || !icao) return;
        setCapturing(true);
        try {
            const container = mapRef.current;
            const containerRect = container.getBoundingClientRect();
            const w = Math.round(containerRect.width);
            const h = Math.round(containerRect.height);
            const canvas = document.createElement('canvas');
            canvas.width = w; canvas.height = h;
            const ctx = canvas.getContext('2d');
            ctx.fillStyle = '#07111e';
            ctx.fillRect(0, 0, w, h);

            // Draw base map tiles
            const tileEls = [...container.querySelectorAll('.leaflet-tile:not(.leaflet-tile-loading)')];
            await Promise.all(tileEls.map(async tile => {
                if (!tile.src) return;
                const tRect = tile.getBoundingClientRect();
                const tx = Math.floor(tRect.left - containerRect.left);
                const ty = Math.floor(tRect.top - containerRect.top);
                const tw = Math.ceil(tRect.width) + 1;
                const th = Math.ceil(tRect.height) + 1;
                try {
                    const resp = await fetch(tile.src, { mode: 'cors' });
                    if (!resp.ok) return;
                    const blob = await resp.blob();
                    const url = URL.createObjectURL(blob);
                    await new Promise(resolve => {
                        const img = new Image();
                        img.onload = () => { ctx.drawImage(img, tx, ty, tw, th); URL.revokeObjectURL(url); resolve(); };
                        img.onerror = () => { URL.revokeObjectURL(url); resolve(); };
                        img.src = url;
                    });
                } catch { }
            }));

            // Draw SVG overlay
            const svgEl = container.querySelector('.leaflet-overlay-pane svg');
            if (svgEl) {
                const svgRect = svgEl.getBoundingClientRect();
                const sx = Math.round(svgRect.left - containerRect.left);
                const sy = Math.round(svgRect.top - containerRect.top);
                const clone = svgEl.cloneNode(true);
                clone.style.transform = ''; clone.style.left = '0'; clone.style.top = '0';
                const svgStr = new XMLSerializer().serializeToString(clone);
                const blob = new Blob([svgStr], { type: 'image/svg+xml;charset=utf-8' });
                const url = URL.createObjectURL(blob);
                await new Promise(resolve => {
                    const img = new Image();
                    img.onload = () => { ctx.drawImage(img, sx, sy, Math.round(svgRect.width), Math.round(svgRect.height)); URL.revokeObjectURL(url); resolve(); };
                    img.onerror = () => { URL.revokeObjectURL(url); resolve(); };
                    img.src = url;
                });
            }

            // Draw obstacle markers
            await drawMarkersOnCanvas(ctx, container, containerRect);

            const dataUrl = canvas.toDataURL('image/jpeg', 0.88);
            const api = (await import('../../api/client')).default;
            await api.post('/npa/screenshot', { icao, image: dataUrl, project_id: project?.id });
            setCapturedNpa(prev => ({ ...prev, [icao]: true }));
        } catch (err) {
            console.error('NPA screenshot error:', err);
        } finally {
            setCapturing(false);
        }
    }, [activeIcao, assessedIcaos, drawMarkersOnCanvas]);

    const clearNpaScreenshot = useCallback(async (icao) => {
        try {
            const { deleteNpaScreenshot } = await import('../../api/client');
            await deleteNpaScreenshot(icao, project?.id);
            setCapturedNpa(prev => {
                const next = { ...prev };
                delete next[icao];
                return next;
            });
        } catch (err) { console.error('Clear NPA screenshot error:', err); }
    }, []);

    // ── Per-runway screenshot capture ─────────────────────────────────────────
    const captureNpaRwyScreenshot = useCallback(async (icao, rwy) => {
        if (!mapRef.current) return;
        setCapturing(true);
        try {
            const container = mapRef.current;
            const containerRect = container.getBoundingClientRect();
            const w = Math.round(containerRect.width);
            const h = Math.round(containerRect.height);
            const canvas = document.createElement('canvas');
            canvas.width = w; canvas.height = h;
            const ctx = canvas.getContext('2d');
            ctx.fillStyle = '#07111e';
            ctx.fillRect(0, 0, w, h);
            // Draw base tiles
            const tileEls = [...container.querySelectorAll('.leaflet-tile:not(.leaflet-tile-loading)')];
            await Promise.all(tileEls.map(async tile => {
                if (!tile.src) return;
                const tRect = tile.getBoundingClientRect();
                try {
                    const resp = await fetch(tile.src, { mode: 'cors' });
                    if (!resp.ok) return;
                    const blob = await resp.blob();
                    const url = URL.createObjectURL(blob);
                    await new Promise(resolve => {
                        const img = new Image();
                        img.onload = () => { ctx.drawImage(img, Math.floor(tRect.left - containerRect.left), Math.floor(tRect.top - containerRect.top), Math.ceil(tRect.width) + 1, Math.ceil(tRect.height) + 1); URL.revokeObjectURL(url); resolve(); };
                        img.onerror = () => { URL.revokeObjectURL(url); resolve(); };
                        img.src = url;
                    });
                } catch { }
            }));
            // Draw SVG overlay
            const svgEl = container.querySelector('.leaflet-overlay-pane svg');
            if (svgEl) {
                const svgRect = svgEl.getBoundingClientRect();
                const clone = svgEl.cloneNode(true);
                clone.style.transform = ''; clone.style.left = '0'; clone.style.top = '0';
                const svgStr = new XMLSerializer().serializeToString(clone);
                const blob = new Blob([svgStr], { type: 'image/svg+xml;charset=utf-8' });
                const url = URL.createObjectURL(blob);
                await new Promise(resolve => {
                    const img = new Image();
                    img.onload = () => { ctx.drawImage(img, Math.round(svgRect.left - containerRect.left), Math.round(svgRect.top - containerRect.top), Math.round(svgRect.width), Math.round(svgRect.height)); URL.revokeObjectURL(url); resolve(); };
                    img.onerror = () => { URL.revokeObjectURL(url); resolve(); };
                    img.src = url;
                });
            }
            // Draw obstacle markers
            await drawMarkersOnCanvas(ctx, container, containerRect);
            const dataUrl = canvas.toDataURL('image/jpeg', 0.88);
            const api = (await import('../../api/client')).default;
            await api.post('/npa/screenshot/rwy', { icao, rwy, image: dataUrl, project_id: project?.id });
            setCapturedNpaRwy(prev => ({ ...prev, [`${icao}_${rwy}`]: true }));
        } catch (err) {
            console.error('NPA RWY screenshot error:', err);
        } finally {
            setCapturing(false);
        }
    }, [drawMarkersOnCanvas]);

    const clearNpaRwyScreenshot = useCallback(async (icao, rwy) => {
        try {
            const api = (await import('../../api/client')).default;
            await api.delete(`/npa/screenshot/rwy/${icao}/${rwy}`, { params: { project_id: project?.id } });
            setCapturedNpaRwy(prev => {
                const next = { ...prev };
                delete next[`${icao}_${rwy}`];
                return next;
            });
        } catch (err) { console.error('Clear NPA RWY screenshot error:', err); }
    }, []);

    const resetNpaAssessment = useCallback((icao) => {
        if (!window.confirm(`Reset NPA assessment for ${icao}? This will remove it from the report.`)) return;
        setNpaResults(prev => {
            const next = { ...prev };
            delete next[icao];
            return next;
        });
        if (activeIcao === icao) setActiveIcao(null);
    }, [activeIcao, setNpaResults]);

    // ── Results for active aerodrome ──────────────────────────────────────────
    const activeData = activeIcao ? npaResults[activeIcao] : null;
    const directions = activeData ? [...new Set((activeData.results || []).map(r => r.approach_direction))] : [];
    if (activeData?.results?.length) {
        markControllingByMargin(activeData.results, r => r.approach_direction, r => r.margin_ft);
    }
    const filteredResults = activeData
        ? (filterDir === 'ALL' ? activeData.results : activeData.results.filter(r => r.approach_direction === filterDir))
        : [];
    const penetrationCount = (activeData?.results || []).filter(r => r.penetrates === true).length;

    const renderResultsContent = () => {
        if (!activeIcao) {
            return (
                <div style={{ padding: '20px 16px', textAlign: 'center', color: T.muted, fontSize: 12 }}>
                    Click <strong style={{ color: T.blue }}>▶ Assess</strong> on an aerodrome, then 📋 to view its results.
                </div>
            );
        }

        if (!activeData) return null;

        const procs = procData[activeIcao] || [];
        const obsForProc = obsWithCoords.map(o => ({ ...o, v_tolerance: o.v_tolerance || 0 }));

        // Segment results split by procedure_type/category — ILS (KMZ-based
        // "ILS (RNP)" transition procedures, until those move to the PA tab),
        // VOR (CONVENTIONAL category — racetrack/procedure-turn/intermediate/
        // final, own tab so it doesn't get lost in LNAV), VNAV, and LNAV (the
        // catch-all for anything else, e.g. RNP AR — same fallback bucket
        // _parse_name_fields uses on the backend).
        const procsIls  = procs.filter(p => p.procedure_type === 'ILS');
        const procsVnav = procs.filter(p => p.procedure_type === 'RNP VNAV');
        const procsVor  = procs.filter(p => p.procedure_category === 'CONVENTIONAL');
        const procsLnav = procs.filter(p => p.procedure_type !== 'ILS' && p.procedure_type !== 'RNP VNAV' && p.procedure_category !== 'CONVENTIONAL');

        const allIlsPens  = procsIls.flatMap(proc => computeProcPenetrations(proc, obsForProc));
        const allLnavPens = procsLnav.flatMap(proc => computeProcPenetrations(proc, obsForProc));
        const allVnavPens = procsVnav.flatMap(proc => computeProcPenetrations(proc, obsForProc));
        const allVorPens  = procsVor.flatMap(proc => computeProcPenetrations(proc, obsForProc));
        const allTaaPens  = procs.flatMap(proc => computeTaaPenetrations(proc, obsForProc));
        const allHoldPens = procs.flatMap(proc => computeHoldingPenetrations(proc, obsForProc));
        const marginKey = r => `${r.procedure_id}_${r.segment}_${r.area_type}_${r.segment_name}`;
        markControllingByMargin(allIlsPens, marginKey, r => r.margin_ft);
        markControllingByMargin(allLnavPens, marginKey, r => r.margin_ft);
        markControllingByMargin(allVnavPens, marginKey, r => r.margin_ft);
        markControllingByMargin(allVorPens, marginKey, r => r.margin_ft);
        markControllingByMargin(allTaaPens, r => r.f_key, r => r.margin_ft);
        markControllingByMargin(allHoldPens, r => r.f_key, r => r.margin_ft);
        const ilsPenCount  = allIlsPens.filter(r  => r.penetrates === true).length;
        const lnavPenCount = allLnavPens.filter(r => r.penetrates === true).length;
        const vnavPenCount = allVnavPens.filter(r => r.penetrates === true).length;
        const vorPenCount  = allVorPens.filter(r  => r.penetrates === true).length;
        const taaPenCount  = allTaaPens.filter(r  => r.penetrates === true).length;
        const holdPenCount = allHoldPens.filter(r => r.penetrates === true).length;

        // Shared lookup for the segment-result tabs — keeps the toggle bar,
        // export button, and table body from repeating the same logic per tab.
        const procTabs = {
            ils:  { procs: procsIls,  pens: allIlsPens,  label: 'ILS (until PA tab)' },
            lnav: { procs: procsLnav, pens: allLnavPens, label: 'LNAV' },
            vnav: { procs: procsVnav, pens: allVnavPens, label: 'VNAV' },
            vor:  { procs: procsVor,  pens: allVorPens,  label: 'VOR' },
        };

        return (
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
                {/* Toggle bar */}
                <div style={{ padding: '4px 16px', borderBottom: `1px solid ${T.border}`, display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
                    <button onClick={() => setResultTab('npa')}
                        style={{ fontSize: 10, padding: '2px 10px', borderRadius: 4, border: 'none', cursor: 'pointer', background: resultTab === 'npa' ? T.blue : 'rgba(255,255,255,0.07)', color: resultTab === 'npa' ? T.white : T.muted }}>
                        📐 NPA Rectangle
                    </button>
                    <button onClick={() => setResultTab('ils')} title="KMZ-based ILS/ILS(RNP) procedures — shown here until moved to the PA tab"
                        style={{ fontSize: 10, padding: '2px 10px', borderRadius: 4, border: 'none', cursor: 'pointer', background: resultTab === 'ils' ? T.blue : 'rgba(255,255,255,0.07)', color: resultTab === 'ils' ? T.white : T.muted }}>
                        🛬 ILS {ilsPenCount > 0 ? `⚠ ${ilsPenCount}` : procsIls.length ? '✓' : ''}
                    </button>
                    <button onClick={() => setResultTab('lnav')}
                        style={{ fontSize: 10, padding: '2px 10px', borderRadius: 4, border: 'none', cursor: 'pointer', background: resultTab === 'lnav' ? T.blue : 'rgba(255,255,255,0.07)', color: resultTab === 'lnav' ? T.white : T.muted }}>
                        🧭 LNAV {lnavPenCount > 0 ? `⚠ ${lnavPenCount}` : procsLnav.length ? '✓' : ''}
                    </button>
                    <button onClick={() => setResultTab('vnav')}
                        style={{ fontSize: 10, padding: '2px 10px', borderRadius: 4, border: 'none', cursor: 'pointer', background: resultTab === 'vnav' ? T.blue : 'rgba(255,255,255,0.07)', color: resultTab === 'vnav' ? T.white : T.muted }}>
                        📈 VNAV {vnavPenCount > 0 ? `⚠ ${vnavPenCount}` : procsVnav.length ? '✓' : ''}
                    </button>
                    <button onClick={() => setResultTab('vor')} title="Conventional (VOR/NDB) procedures — racetrack/procedure-turn/intermediate/final segments"
                        style={{ fontSize: 10, padding: '2px 10px', borderRadius: 4, border: 'none', cursor: 'pointer', background: resultTab === 'vor' ? T.blue : 'rgba(255,255,255,0.07)', color: resultTab === 'vor' ? T.white : T.muted }}>
                        📻 VOR {vorPenCount > 0 ? `⚠ ${vorPenCount}` : procsVor.length ? '✓' : ''}
                    </button>
                    <button onClick={() => setResultTab('taa')}
                        style={{ fontSize: 10, padding: '2px 10px', borderRadius: 4, border: 'none', cursor: 'pointer', background: resultTab === 'taa' ? T.blue : 'rgba(255,255,255,0.07)', color: resultTab === 'taa' ? T.white : T.muted }}>
                        ✈ TAA {taaPenCount > 0 ? `⚠ ${taaPenCount}` : allTaaPens.length > 0 ? '✓' : ''}
                    </button>
                    <button onClick={() => setResultTab('hold')}
                        style={{ fontSize: 10, padding: '2px 10px', borderRadius: 4, border: 'none', cursor: 'pointer', background: resultTab === 'hold' ? T.blue : 'rgba(255,255,255,0.07)', color: resultTab === 'hold' ? T.white : T.muted }}>
                        🔄 Hold {holdPenCount > 0 ? `⚠ ${holdPenCount}` : allHoldPens.length > 0 ? '✓' : ''}
                    </button>
                    {resultTab === 'npa' && (
                        <div style={{ display: 'flex', gap: 4, marginLeft: 8 }}>
                            {['ALL', ...directions].map(d => (
                                <button key={d} onClick={() => setFilterDir(d)} style={{
                                    padding: '2px 8px', borderRadius: 4, border: 'none', cursor: 'pointer', fontSize: 10,
                                    background: filterDir === d ? T.blue : 'rgba(255,255,255,0.07)',
                                    color: filterDir === d ? T.white : T.muted, fontWeight: filterDir === d ? 700 : 400
                                }}>{d === 'ALL' ? 'All' : `RWY ${d}`}</button>
                            ))}
                        </div>
                    )}
                    <div style={{ flex: 1 }} />
                    {((procTabs[resultTab] && procTabs[resultTab].pens.length > 0) || (resultTab === 'npa' && filteredResults.length > 0) || (resultTab === 'taa' && allTaaPens.length > 0) || (resultTab === 'hold' && allHoldPens.length > 0)) && (
                        <button
                            onClick={() => {
                                if (procTabs[resultTab]) {
                                    const headers = ["Obstacle", "Procedure", "RWY", "Segment", "Area", "Elev (m)", "V-Acc (m)", "MOC (m)", "Total Elev (ft)", "MOCA/OCA (ft)", "Margin (ft)", "Status", "Controlling"];
                                    const rows = procTabs[resultTab].pens.map(r => [
                                        r.obstacle_name,
                                        r.procedure_name,
                                        r.runway || '-',
                                        r.segment,
                                        r.area_type === 'secondary' ? 'Secondary' : 'Primary',
                                        r.obs_elev_m,
                                        r.v_accuracy_m,
                                        r.moc_m,
                                        r.total_elev_ft ?? r.total_elev_m,
                                        r.moca_ft ?? '-',
                                        r.margin_ft !== null && r.margin_ft !== undefined ? (r.margin_ft >= 0 ? `+${r.margin_ft}` : r.margin_ft) : '-',
                                        r.penetrates === null ? 'NO MOCA' : r.penetrates ? 'PENETRATES' : 'CLEAR',
                                        r.isControlling ? 'Yes' : ''
                                    ]);
                                    exportToCsv(headers, rows, `${activeIcao}_NPA_${resultTab.toUpperCase()}_Assessment.csv`);
                                } else if (resultTab === 'npa') {
                                    const headers = ["Obstacle", "RWY", "Elev (m)", "V-Acc (m)", "MOC (m)", "Effective (m)", "OCA (m)", "Margin (m)", "Status", "Controlling"];
                                    const rows = filteredResults.map(r => {
                                        const vtol = parseFloat(r.v_tolerance_m ?? r.v_tolerance ?? 0);
                                        const elev = parseFloat(r.obstacle_elev_m ?? 0);
                                        const moc = 75;
                                        const effective = parseFloat(r.effective_m ?? (elev + vtol + moc));
                                        const oca = r.oca_m ?? null;
                                        const margin = oca !== null ? (oca - effective).toFixed(1) : null;
                                        return [
                                            r.obstacle_name,
                                            `RWY ${r.approach_direction}`,
                                            elev.toFixed(1),
                                            vtol.toFixed(1),
                                            75,
                                            effective.toFixed(1),
                                            oca !== null ? oca.toFixed(1) : '-',
                                            margin !== null ? margin : '-',
                                            r.penetrates === null ? 'No Minima' : r.penetrates ? 'PENETRATES' : 'CLEAR',
                                            r.isControlling ? 'Yes' : ''
                                        ];
                                    });
                                    exportToCsv(headers, rows, `${activeIcao}_NPA_Rectangle_Assessment.csv`);
                                } else if (resultTab === 'taa') {
                                    const headers = ["Obstacle", "RWY", "TAA Name", "Elev (m)", "V-Acc (m)", "MOC (m)", "Total Elev (ft)", "TAA Alt (ft)", "Margin (ft)", "Status", "Controlling"];
                                    const rows = allTaaPens.map(r => [
                                        r.obstacle_name,
                                        r.rwy,
                                        r.sector_name,
                                        r.obs_elev_m,
                                        r.v_accuracy_m,
                                        300,
                                        r.total_elev_ft,
                                        r.taa_alt_ft ?? '-',
                                        r.margin_ft !== null ? (r.margin_ft >= 0 ? `+${r.margin_ft}` : r.margin_ft) : '-',
                                        r.penetrates === null ? 'NO TAA ALT' : r.penetrates ? 'PENETRATES' : 'CLEAR',
                                        r.isControlling ? 'Yes' : ''
                                    ]);
                                    exportToCsv(headers, rows, `${activeIcao}_NPA_TAA_Assessment.csv`);
                                } else if (resultTab === 'hold') {
                                    const headers = ["Obstacle", "Procedure", "RWY", "Holding", "Band", "Elev (m)", "V-Acc (m)", "MOC (m)", "Total Elev (ft)", "Hold Alt (ft)", "Margin (ft)", "Status", "Controlling"];
                                    const rows = allHoldPens.map(r => [
                                        r.obstacle_name,
                                        r.procedure_name,
                                        r.rwy,
                                        r.holding_name,
                                        r.band_label,
                                        r.obs_elev_m,
                                        r.v_accuracy_m,
                                        r.moc_m,
                                        r.total_elev_ft,
                                        r.hold_ft ?? '-',
                                        r.margin_ft !== null ? (r.margin_ft >= 0 ? `+${r.margin_ft}` : r.margin_ft) : '-',
                                        r.penetrates === null ? 'NO HOLD ALT' : r.penetrates ? 'PENETRATES' : 'CLEAR',
                                        r.isControlling ? 'Yes' : ''
                                    ]);
                                    exportToCsv(headers, rows, `${activeIcao}_NPA_Holding_Assessment.csv`);
                                }
                            }}
                            style={{
                                fontSize: 10, padding: '2px 10px', borderRadius: 4, border: 'none', cursor: 'pointer',
                                background: T.blue, color: T.white, display: 'flex', alignItems: 'center', gap: 4, marginRight: 8
                            }}
                        >
                            📥 Export CSV
                        </button>
                    )}
                    {penetrationCount > 0 && resultTab === 'npa' && (
                        <div style={{ fontSize: 11, color: T.danger, fontWeight: 700, background: T.danger + '22', border: `1px solid ${T.danger}44`, borderRadius: 5, padding: '2px 8px' }}>
                            ⚠ {penetrationCount} NPA rect penetration{penetrationCount > 1 ? 's' : ''}
                        </div>
                    )}
                </div>

                <div style={{ flex: 1, overflowY: 'auto' }}>
                    {/* ── Segment Penetration Tables (ILS / LNAV / VNAV) ── */}
                    {procTabs[resultTab] && (
                        procTabs[resultTab].procs.length === 0 ? (
                            <div style={{ padding: '20px 16px', textAlign: 'center', color: T.muted, fontSize: 12 }}>
                                No {procTabs[resultTab].label} procedure stored for {activeIcao}.
                                Go to Data Manager → 🛫 Procedures to upload one.
                            </div>
                        ) : procTabs[resultTab].pens.length === 0 ? (
                            <div style={{ padding: '20px 16px', textAlign: 'center', color: T.safe, fontSize: 12 }}>
                                ✓ No obstacles fall within any {procTabs[resultTab].label} protection area.
                            </div>
                        ) : (
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                                <thead>
                                    <tr style={{ background: T.bg0, position: 'sticky', top: 0, zIndex: 1 }}>
                                        {[
                                            'Obstacle', 'Procedure', 'RWY', 'Segment', 'Area', 'Elev (m)',
                                            'V-Acc (m)', 'MOC (m)', 'Total Elev (ft)', 'MOCA/OCA (ft)', 'Margin (ft)', 'Status'
                                        ].map(h => (
                                            <th key={h} style={{ padding: '6px 8px', textAlign: 'left', color: T.muted, fontWeight: 600, borderBottom: `1px solid ${T.border}`, whiteSpace: 'nowrap', fontSize: 10 }}>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {procTabs[resultTab].pens.map((r, i) => {
                                        const segCol = SEG_COLORS[r.segment] || '#888';
                                        const areaCol = r.area_type === 'secondary' ? '#F4A300' : '#2EC98B';
                                        const pen = r.penetrates;
                                        return (
                                            <tr key={i} style={{ borderBottom: `1px solid ${T.border}22`, background: pen ? T.danger + '11' : 'transparent' }}>
                                                <td style={{ padding: '5px 8px', color: T.white, fontWeight: 600, whiteSpace: 'nowrap' }}>
                                                    {r.obstacle_name} {r.isControlling && <span style={{ color: T.blue, fontSize: 9 }}>[Controlling]</span>}
                                                </td>
                                                <td style={{ padding: '5px 8px', color: T.muted, fontSize: 10 }}>{r.procedure_name}</td>
                                                <td style={{ padding: '5px 8px', color: T.blue, fontWeight: 700 }}>{r.runway || '—'}</td>
                                                <td style={{ padding: '5px 8px' }}>
                                                    <span style={{ background: segCol + '33', color: segCol, border: `1px solid ${segCol}55`, borderRadius: 4, padding: '1px 6px', fontSize: 10, fontWeight: 700, textTransform: 'uppercase' }}>
                                                        {r.segment}
                                                    </span>
                                                </td>
                                                <td style={{ padding: '5px 8px' }}>
                                                    <span style={{ background: areaCol + '22', color: areaCol, border: `1px solid ${areaCol}55`, borderRadius: 4, padding: '1px 6px', fontSize: 10, fontWeight: 600 }}>
                                                        {r.area_type === 'secondary' ? 'Secondary' : 'Primary'}
                                                    </span>
                                                </td>
                                                <td style={{ padding: '5px 8px', color: T.white }}>{r.obs_elev_m}</td>
                                                <td style={{ padding: '5px 8px', color: T.muted }}>{r.v_accuracy_m}</td>
                                                <td style={{ padding: '5px 8px', color: '#F4D03F', fontWeight: 700 }}>
                                                    {r.moc_m}
                                                    {r.area_type === 'secondary' && <span style={{ fontSize: 9, color: T.muted, marginLeft: 3 }}>(sy)</span>}
                                                </td>
                                                <td style={{ padding: '5px 8px', color: pen ? T.danger : T.white, fontWeight: pen ? 700 : 400 }}>{r.total_elev_ft ?? r.total_elev_m}</td>
                                                <td style={{ padding: '5px 8px', color: r.moca_ft ? '#F4D03F' : T.muted }}>{r.moca_ft ?? '—'}</td>
                                                <td style={{ padding: '5px 8px', color: pen ? T.danger : (r.margin_ft !== null && r.margin_ft !== undefined ? T.safe : T.muted), fontWeight: 700 }}>
                                                    {r.margin_ft !== null && r.margin_ft !== undefined ? (r.margin_ft >= 0 ? `+${r.margin_ft}` : r.margin_ft) : '—'}
                                                </td>
                                                <td style={{ padding: '5px 8px', fontSize: 10, color: pen === null ? T.muted : pen ? T.danger : T.safe, fontWeight: 700 }}>
                                                    {pen === null ? 'NO MOCA' : pen ? '⚠ PENETRATES' : '✓ CLEAR'}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        )
                    )}

                    {/* ── NPA Rectangle Table ── */}
                    {resultTab === 'npa' && (
                        filteredResults.length === 0 ? (
                            <div style={{ padding: '20px 12px', textAlign: 'center', color: T.muted, fontSize: 12 }}>
                                {activeData.results.length === 0 ? 'No obstacles found within the NPA assessment areas.' : 'No obstacles for selected direction.'}
                            </div>
                        ) : (
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                                <thead>
                                    <tr style={{ background: T.bg0, position: 'sticky', top: 0, zIndex: 1 }}>
                                        {[
                                            'Obstacle', 'RWY', 'Elev (m)',
                                            'V-Acc (m)', 'MOC (m)', 'Effective (m)',
                                            'OCA (m)', 'Margin (m)', 'Status',
                                        ].map(h => (
                                            <th key={h} style={{ padding: '7px 10px', textAlign: 'left', color: T.muted, fontWeight: 600, borderBottom: `1px solid ${T.border}`, whiteSpace: 'nowrap', fontSize: 10 }}>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredResults.map((r, i) => {
                                        const pen = r.penetrates;
                                        const vtol = parseFloat(r.v_tolerance_m ?? r.v_tolerance ?? 0);
                                        const elev = parseFloat(r.obstacle_elev_m ?? 0);
                                        const moc  = 75;   // Final approach MOC — fixed
                                        const effective = parseFloat(r.effective_m ?? (elev + vtol + moc));
                                        const oca = r.oca_m ?? null;
                                        const margin = oca !== null ? (oca - effective).toFixed(1) : null;
                                        return (
                                            <tr key={i} style={{ borderBottom: `1px solid ${T.border}22`, background: pen ? T.danger + '11' : 'transparent' }}>
                                                <td style={{ padding: '7px 10px', color: T.white, fontWeight: 600, whiteSpace: 'nowrap' }}>
                                                    {r.obstacle_name}
                                                    {r.generic_och && <span style={{ marginLeft: 6, background: '#F4A30022', color: '#F4A300', border: '1px solid #F4A30066', borderRadius: 4, padding: '1px 6px', fontSize: 9, fontWeight: 700 }}>Generic OCH</span>}
                                                    {r.isControlling && <span style={{ marginLeft: 6, color: T.blue, fontSize: 9 }}>[Controlling]</span>}
                                                </td>
                                                <td style={{ padding: '7px 10px', color: T.blue, fontWeight: 700 }}>RWY {r.approach_direction}</td>
                                                <td style={{ padding: '7px 10px', color: T.white }}>{elev.toFixed(1)}</td>
                                                <td style={{ padding: '7px 10px', color: T.muted }}>{vtol.toFixed(1)}</td>
                                                <td style={{ padding: '7px 10px', color: '#F4D03F', fontWeight: 700 }}>75</td>
                                                <td style={{ padding: '7px 10px', color: pen ? T.danger : T.white, fontWeight: pen ? 700 : 400 }}>{effective.toFixed(1)}</td>
                                                <td style={{ padding: '7px 10px', color: oca !== null ? '#F4D03F' : T.muted }}>{oca !== null ? oca.toFixed(1) : '—'}</td>
                                                <td style={{ padding: '7px 10px', color: margin !== null ? (parseFloat(margin) < 0 ? T.danger : T.safe) : T.muted, fontWeight: 700 }}>
                                                    {margin !== null ? margin : '—'}
                                                </td>
                                                <td style={{ padding: '7px 10px', color: pen ? T.danger : T.safe, fontSize: 10, fontWeight: pen ? 700 : 400 }}>
                                                    {pen === null ? 'No Minima' : pen ? '⚠ PENETRATES' : '✓ CLEAR'}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        )
                    )}

                    {/* ── TAA Penetration Table ── */}
                    {resultTab === 'taa' && (
                        procs.length === 0 ? (
                            <div style={{ padding: '20px 16px', textAlign: 'center', color: T.muted, fontSize: 12 }}>
                                No procedure stored for {activeIcao}. Upload one in Data Manager → 🛫 Procedures.
                            </div>
                        ) : allTaaPens.length === 0 ? (
                            <div style={{ padding: '20px 16px', textAlign: 'center', color: T.safe, fontSize: 12 }}>
                                {collectTAA(procs[0]?.json_layers || {}).length === 0
                                    ? '⚠ No TAA sectors found in procedure (re-parse to update json_layers).'
                                    : '✓ No obstacles fall within any TAA sector.'}
                            </div>
                        ) : (
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                                <thead>
                                    <tr style={{ background: T.bg0, position: 'sticky', top: 0, zIndex: 1 }}>
                                        {['Obstacle', 'RWY', 'TAA Name', 'Elev (m)', 'V-Acc (m)', 'MOC (m)', 'Total Elev (ft)', 'TAA Alt (ft)', 'Margin (ft)', 'Status'].map(h => (
                                            <th key={h} style={{ padding: '6px 8px', textAlign: 'left', color: T.muted, fontWeight: 600, borderBottom: `1px solid ${T.border}`, whiteSpace: 'nowrap', fontSize: 10 }}>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {allTaaPens.map((r, i) => {
                                        const pen = r.penetrates;
                                        return (
                                            <tr key={i} style={{ borderBottom: `1px solid ${T.border}22`, background: pen ? T.danger + '11' : 'transparent' }}>
                                                <td style={{ padding: '5px 8px', color: T.white, fontWeight: 600, whiteSpace: 'nowrap' }}>
                                                    {r.obstacle_name} {r.isControlling && <span style={{ color: T.blue, fontSize: 9 }}>[Controlling]</span>}
                                                </td>
                                                <td style={{ padding: '5px 8px', color: T.blue, fontWeight: 700 }}>{r.rwy}</td>
                                                <td style={{ padding: '5px 8px', color: '#9B59B6', fontWeight: 600 }}>{r.sector_name}</td>
                                                <td style={{ padding: '5px 8px', color: T.white }}>{r.obs_elev_m}</td>
                                                <td style={{ padding: '5px 8px', color: T.muted }}>{r.v_accuracy_m}</td>
                                                <td style={{ padding: '5px 8px', color: '#F4D03F', fontWeight: 700 }}>300</td>
                                                <td style={{ padding: '5px 8px', color: pen ? T.danger : T.white, fontWeight: pen ? 700 : 400 }}>{r.total_elev_ft}</td>
                                                <td style={{ padding: '5px 8px', color: r.taa_alt_ft ? '#F4D03F' : T.muted }}>{r.taa_alt_ft ?? '—'}</td>
                                                <td style={{ padding: '5px 8px', color: pen ? T.danger : (r.margin_ft !== null ? T.safe : T.muted), fontWeight: 700 }}>
                                                    {r.margin_ft !== null ? (r.margin_ft >= 0 ? `+${r.margin_ft}` : r.margin_ft) : '—'}
                                                </td>
                                                <td style={{ padding: '5px 8px', fontSize: 10, color: pen === null ? T.muted : pen ? T.danger : T.safe, fontWeight: 700 }}>
                                                    {pen === null ? 'NO TAA ALT' : pen ? '⚠ PENETRATES' : '✓ CLEAR'}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        )
                    )}

                    {/* ── Holding Area Penetration Table ── */}
                    {resultTab === 'hold' && (
                        procs.length === 0 ? (
                            <div style={{ padding: '20px 16px', textAlign: 'center', color: T.muted, fontSize: 12 }}>
                                No procedure stored for {activeIcao}. Upload one in Data Manager → 🛫 Procedures.
                            </div>
                        ) : allHoldPens.length === 0 ? (
                            <div style={{ padding: '20px 16px', textAlign: 'center', color: T.safe, fontSize: 12 }}>
                                {collectHolding(procs[0]?.json_layers || {}).length === 0
                                    ? '⚠ No holding areas found in procedure (re-parse to update json_layers).'
                                    : '✓ No obstacles fall within any holding area.'}
                            </div>
                        ) : (
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                                <thead>
                                    <tr style={{ background: T.bg0, position: 'sticky', top: 0, zIndex: 1 }}>
                                        {['Obstacle', 'RWY', 'Holding', 'Band', 'Elev (m)', 'V-Acc (m)', 'MOC (m)', 'Total Elev (ft)', 'Hold Alt (ft)', 'Margin (ft)', 'Status'].map(h => (
                                            <th key={h} style={{ padding: '6px 8px', textAlign: 'left', color: T.muted, fontWeight: 600, borderBottom: `1px solid ${T.border}`, whiteSpace: 'nowrap', fontSize: 10 }}>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {allHoldPens.map((r, i) => {
                                        const pen = r.penetrates;
                                        const bandCol = r.band === 'primary' ? '#2EC98B' : '#F4A300';
                                        return (
                                            <tr key={i} style={{ borderBottom: `1px solid ${T.border}22`, background: pen ? T.danger + '11' : 'transparent' }}>
                                                <td style={{ padding: '5px 8px', color: T.white, fontWeight: 600, whiteSpace: 'nowrap' }}>
                                                    {r.obstacle_name} {r.isControlling && <span style={{ color: T.blue, fontSize: 9 }}>[Controlling]</span>}
                                                </td>
                                                <td style={{ padding: '5px 8px', color: T.blue, fontWeight: 700 }}>{r.rwy}</td>
                                                <td style={{ padding: '5px 8px', color: '#9B59B6', fontWeight: 600 }}>{r.holding_name}</td>
                                                <td style={{ padding: '5px 8px' }}>
                                                    <span style={{ background: bandCol + '22', color: bandCol, border: `1px solid ${bandCol}55`, borderRadius: 4, padding: '1px 6px', fontSize: 10, fontWeight: 600 }}>
                                                        {r.band_label}
                                                    </span>
                                                </td>
                                                <td style={{ padding: '5px 8px', color: T.white }}>{r.obs_elev_m}</td>
                                                <td style={{ padding: '5px 8px', color: T.muted }}>{r.v_accuracy_m}</td>
                                                <td style={{ padding: '5px 8px', color: '#F4D03F', fontWeight: 700 }}>{r.moc_m}</td>
                                                <td style={{ padding: '5px 8px', color: pen ? T.danger : T.white, fontWeight: pen ? 700 : 400 }}>{r.total_elev_ft}</td>
                                                <td style={{ padding: '5px 8px', color: r.hold_ft ? '#F4D03F' : T.muted }}>{r.hold_ft ?? '—'}</td>
                                                <td style={{ padding: '5px 8px', color: pen ? T.danger : (r.margin_ft !== null ? T.safe : T.muted), fontWeight: 700 }}>
                                                    {r.margin_ft !== null ? (r.margin_ft >= 0 ? `+${r.margin_ft}` : r.margin_ft) : '—'}
                                                </td>
                                                <td style={{ padding: '5px 8px', fontSize: 10, color: pen === null ? T.muted : pen ? T.danger : T.safe, fontWeight: 700 }}>
                                                    {pen === null ? 'NO HOLD ALT' : pen ? '⚠ PENETRATES' : '✓ CLEAR'}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        )
                    )}
                </div>
            </div>
        );
    };

    return (
        <div style={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
            {/* ── Left panel ── */}
            <div style={{ width: 310, flexShrink: 0, background: T.bg1, backdropFilter: 'blur(var(--blur-md))', WebkitBackdropFilter: 'blur(var(--blur-md))', borderRight: `1px solid ${T.border}`, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

                {/* Header */}
                <div style={{ padding: '10px 14px', borderBottom: `1px solid ${T.border}` }}>
                    <div style={{ fontSize: 12, fontWeight: 800, color: T.white, marginBottom: 4 }}>🛬 NPA Aerodromes ({validArps.length})</div>
                    <div style={{ fontSize: 10, color: T.muted, lineHeight: 1.4 }}>
                        4 NM wide · 6 NM beyond each threshold<br />
                        Penetration = OBS + V-TOL + 75 m &gt; OCA
                    </div>
                    <button
                        onClick={handleExportAixm}
                        disabled={exportingAixm}
                        style={{ marginTop: 8, padding: '4px 8px', borderRadius: 4, border: `1px solid ${T.blue}44`, cursor: exportingAixm ? 'default' : 'pointer', background: `${T.blue}11`, color: T.blue, fontSize: 10, fontWeight: 700, opacity: exportingAixm ? 0.6 : 1 }}
                        title="Export obstacles, aerodrome data, waypoints, approach procedures (ILS/VOR/LNAV) and TAA as AIXM 5.1"
                    >
                        {exportingAixm ? '⏳…' : '📤 AIXM'}
                    </button>
                </div>

                <div style={{ flex: 1, overflowY: 'auto', paddingBottom: 8 }}>
                    {validArps.length === 0 && (
                        <div style={{ padding: 16, fontSize: 12, color: T.muted }}>
                            {npaAerodromes.length === 0 ? 'Loading NPA data…' : 'No NPA aerodromes within project radius.'}
                        </div>
                    )}

                    {validArps.map((arp, i) => {
                        const icao = arp.icao?.toUpperCase();
                        const isAssessed = !!npaResults[icao];
                        const isAssessing = !!assessing[icao];
                        const isActive = icao === activeIcao;
                        const rectColor = getRectColor(icao, assessedIcaos);
                        const data = npaResults[icao];
                        const pairs = data ? (data.polygons?.features || []).map(f => f.properties?.rwy_pair).filter(Boolean) : [];
                        const allVisible = visNpa[`${icao}_ALL`] !== false;

                        return (
                            <div key={i} style={{ borderBottom: `1px solid ${T.border}22` }}>
                                {/* Aerodrome row */}
                                <div style={{ padding: '8px 14px', display: 'flex', alignItems: 'center', gap: 8 }}>
                                    {/* All-surfaces toggle */}
                                    {isAssessed && (
                                        <input
                                            type="checkbox"
                                            checked={allVisible}
                                            onChange={e => {
                                                const val = e.target.checked;
                                                setVisNpa(prev => {
                                                    const next = { ...prev, [`${icao}_ALL`]: val };
                                                    pairs.forEach(p => { next[`${icao}_${p}`] = val; });
                                                    return next;
                                                });
                                            }}
                                            style={{ accentColor: rectColor, cursor: 'pointer' }}
                                            title="Toggle all NPA surfaces for this aerodrome"
                                        />
                                    )}
                                    {/* Colour swatch */}
                                    {isAssessed && <div style={{ width: 8, height: 8, borderRadius: 2, background: rectColor, flexShrink: 0 }} />}
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontSize: 12, fontWeight: 700, color: isActive ? rectColor : isAssessed ? T.white : T.blue }}>{icao}</div>
                                        <div style={{ fontSize: 10, color: T.muted }}>{arp.name} · {arp.distance_km?.toFixed(0)} km</div>
                                    </div>
                                    <button
                                        onClick={() => { runAssessment(icao); }}
                                        disabled={isAssessing}
                                        style={{
                                            padding: '3px 10px', fontSize: 10, fontWeight: 700,
                                            background: isAssessed ? 'rgba(46,201,139,0.15)' : 'rgba(58,138,196,0.2)',
                                            border: `1px solid ${isAssessed ? T.safe : T.blue}44`,
                                            borderRadius: 5, color: isAssessed ? T.safe : T.blue,
                                            cursor: isAssessing ? 'wait' : 'pointer',
                                        }}
                                        title={isAssessed ? 'Re-run assessment' : 'Load NPA assessment'}
                                    >
                                        {isAssessing ? '⟳' : isAssessed ? '↺ Re-run' : '⬇ Load'}
                                    </button>
                                    {isAssessed && (
                                        <div style={{ display: 'flex', gap: 2 }}>
                                            <button
                                                onClick={() => captureNpaScreenshot(icao)}
                                                disabled={capturing}
                                                title={`Capture NPA map for ${icao} – save for report`}
                                                style={{
                                                    padding: '3px 8px', fontSize: 10, borderRadius: 4, cursor: capturing ? 'wait' : 'pointer',
                                                    background: capturedNpa[icao] ? 'rgba(46,201,139,0.15)' : 'rgba(255,255,255,0.05)',
                                                    border: `1px solid ${capturedNpa[icao] ? T.safe : T.border}`,
                                                    color: capturedNpa[icao] ? T.safe : T.muted,
                                                }}
                                            >
                                                {capturing && (activeIcao === icao || (!activeIcao && assessedIcaos[0] === icao)) ? '⟳ Saving...' : (capturedNpa[icao] ? '✓ Captured' : 'Capture Screenshot')}
                                            </button>
                                            {capturedNpa[icao] && (
                                                <button title="Clear screenshot" onClick={() => clearNpaScreenshot(icao)} style={{
                                                    background: 'rgba(255,255,255,0.05)', border: `1px solid ${T.border}`,
                                                    color: T.danger, borderRadius: 4, padding: '3px 5px', cursor: 'pointer', fontSize: 12, lineHeight: 1
                                                }}>✕</button>
                                            )}
                                        </div>
                                    )}
                                    {isAssessed && (
                                        <div style={{ display: 'flex', gap: 2 }}>
                                            <button
                                                onClick={() => setActiveIcao(isActive ? null : icao)}
                                                style={{ padding: '3px 8px', fontSize: 10, background: isActive ? 'rgba(244,208,63,0.15)' : 'rgba(255,255,255,0.05)', border: `1px solid ${isActive ? '#F4D03F' : T.border}`, borderRadius: 4, color: isActive ? '#F4D03F' : T.muted, cursor: 'pointer' }}
                                                title="View results table"
                                            >
                                                📋
                                            </button>
                                            <button title="Reset Assessment (remove from report)" onClick={() => resetNpaAssessment(icao)} style={{
                                                padding: '3px 6px', borderRadius: 4, border: `1px solid ${T.border}`,
                                                background: 'transparent', color: T.danger, cursor: 'pointer', fontSize: 12,
                                            }}>🗑️</button>
                                        </div>
                                    )}
                                </div>

                                {/* Per-runway-pair toggles */}
                                {isAssessed && pairs.length > 0 && (
                                    <div style={{ padding: '0 14px 6px 36px', display: 'flex', flexDirection: 'column', gap: 3 }}>
                                        {pairs.map(pair => {
                                            const visKey = `${icao}_${pair}`;
                                            const vis = visNpa[visKey] !== false;
                                            const disabled = !allVisible;
                                            const pairResults = (data.results || []).filter(r => r.approach_direction === pair);
                                            const hasPen = pairResults.some(r => r.penetrates);

                                            return (
                                                <label key={pair} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 11 }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={vis}
                                                        disabled={disabled}
                                                        onChange={e => {
                                                            const val = e.target.checked;
                                                            setVisNpa(prev => {
                                                                const next = { ...prev, [visKey]: val };
                                                                if (val) next[`${icao}_ALL`] = true;
                                                                return next;
                                                            });
                                                        }}
                                                        style={{ accentColor: rectColor, cursor: 'pointer' }}
                                                    />
                                                    <span style={{ color: vis ? T.white : T.muted, opacity: disabled ? 0.5 : 1 }}>RWY {pair}</span>
                                                    {hasPen && <span style={{ fontSize: 9, color: T.danger, fontWeight: 700 }}>⚠</span>}
                                                    {!hasPen && pairResults.length > 0 && <span style={{ fontSize: 9, color: T.safe }}>✓</span>}
                                                </label>
                                            );
                                        })}
                                    </div>
                                )}

                {/* OCA minima table */}
                                {isAssessed && data?.minima?.length > 0 && (
                                    <div style={{ padding: '0 14px 6px 36px' }}>
                                        <button
                                            onClick={() => setExpandedMinima(prev => ({ ...prev, [icao]: !prev[icao] }))}
                                            style={{ fontSize: 10, color: T.muted, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                                        >
                                            {expandedMinima[icao] ? '▲' : '▼'} OCA minima ({data.minima.length} rwys)
                                        </button>
                                        {expandedMinima[icao] && (
                                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10, marginTop: 4 }}>
                                                <thead>
                                                    <tr style={{ background: T.bg0 }}>
                                                        {['RWY', 'A', 'B', 'C', 'D', 'VPA°'].map(h => (
                                                            <th key={h} style={{ padding: '3px 5px', color: T.muted, textAlign: 'center', borderBottom: `1px solid ${T.border}` }}>{h}</th>
                                                        ))}
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {data.minima.map((m, mi) => (
                                                        <tr key={mi} style={{ borderBottom: `1px solid ${T.border}22` }}>
                                                            <td style={{ padding: '2px 5px', color: T.white, fontWeight: 700 }}>{m.runway}</td>
                                                            {['cat_a', 'cat_b', 'cat_c', 'cat_d'].map(c => (
                                                                <td key={c} style={{ padding: '2px 5px', color: T.muted, textAlign: 'center' }}>{m[c] ?? '—'}</td>
                                                            ))}
                                                            <td style={{ padding: '2px 5px', color: '#F4D03F', textAlign: 'center', fontWeight: 700 }}>{m.vpa ?? '—'}°</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        )}
                                    </div>
                                )}

                                {/* Procedure layer trees — grouped by runway */}
                                {isAssessed && (procData[icao]?.length > 0) && (() => {
                                    // Normalize runway: strip 'RWY' prefix, zero-pad
                                    const normRwy = (r) => {
                                        const s = String(r || '').replace(/^RWY\s*/i, '').trim();
                                        const num = s.replace(/\D+$/, '');
                                        const suf = s.slice(num.length);
                                        return num ? String(parseInt(num, 10)).padStart(2, '0') + suf.toUpperCase() : s.padStart(2, '0');
                                    };
                                    // Group procedures by zero-padded runway
                                    const byRwy = {};
                                    (procData[icao] || []).forEach(proc => {
                                        const rwy = normRwy(proc.runway);
                                        if (!byRwy[rwy]) byRwy[rwy] = [];
                                        byRwy[rwy].push(proc);
                                    });
                                    const rwys = Object.keys(byRwy).sort();
                                    return (
                                        <div style={{ margin: '4px 14px 8px 14px', background: 'rgba(255,255,255,0.03)', border: `1px solid ${T.border}22`, borderRadius: 6, overflow: 'hidden' }}>
                                            <div style={{ padding: '5px 10px', borderBottom: `1px solid ${T.border}22`, fontSize: 10, fontWeight: 700, color: T.muted, display: 'flex', alignItems: 'center', gap: 6 }}>
                                                <span>🛫</span>
                                                <span>PROCEDURE LAYERS</span>
                                                <span style={{ marginLeft: 'auto', fontSize: 9, fontWeight: 400 }}>
                                                    {procData[icao].length} procedure{procData[icao].length > 1 ? 's' : ''}
                                                </span>
                                            </div>
                                            <div style={{ padding: '4px 6px 6px', overflowX: 'hidden' }}>
                                                {rwys.map(rwy => {
                                                    const procs = byRwy[rwy];
                                                    const rwyKey = `${icao}_${rwy}`;
                                                    const captured = capturedNpaRwy[rwyKey];
                                                    return (
                                                        <div key={rwy} style={{ marginBottom: 6, border: `1px solid ${T.border}22`, borderRadius: 4 }}>
                                                            {/* Runway header with capture button */}
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 8px', background: 'rgba(255,255,255,0.04)', borderBottom: `1px solid ${T.border}22` }}>
                                                                <span style={{ fontSize: 11, fontWeight: 800, color: T.white, flex: 1 }}>RWY {rwy}</span>
                                                                <span style={{ fontSize: 9, color: T.muted }}>{procs.length} proc{procs.length > 1 ? 's' : ''}</span>
                                                                {/* Per-runway screenshot capture button */}
                                                                <button
                                                                    onClick={() => captureNpaRwyScreenshot(icao, rwy)}
                                                                    disabled={capturing}
                                                                    title={`Capture map for RWY ${rwy} – save for report`}
                                                                    style={{
                                                                        padding: '2px 6px', fontSize: 9, borderRadius: 3, cursor: capturing ? 'wait' : 'pointer',
                                                                        background: captured ? 'rgba(46,201,139,0.15)' : 'rgba(255,255,255,0.05)',
                                                                        border: `1px solid ${captured ? T.safe : T.border}`,
                                                                        color: captured ? T.safe : T.muted,
                                                                    }}
                                                                >
                                                                    {capturing ? '⟳ Saving...' : captured ? '✓ Captured' : 'Capture Screenshot'}
                                                                </button>
                                                                {captured && (
                                                                    <button
                                                                        title="Clear screenshot"
                                                                        onClick={() => clearNpaRwyScreenshot(icao, rwy)}
                                                                        style={{ background: 'rgba(255,255,255,0.05)', border: `1px solid ${T.border}`, color: T.danger, borderRadius: 3, padding: '2px 4px', cursor: 'pointer', fontSize: 11, lineHeight: 1 }}
                                                                    >✕</button>
                                                                )}
                                                            </div>
                                                            {/* Procedures under this runway */}
                                                            <div style={{ padding: '4px 6px 4px' }}>
                                                                {procs.map((proc, pi) => {
                                                                    const vis = procTreeVis[icao] || {};
                                                                    const handleChange = (node, checked) => {
                                                                        setProcTreeVis(prev => ({
                                                                            ...prev,
                                                                            [icao]: cascadeVis(prev[icao] || {}, node, checked),
                                                                        }));
                                                                    };
                                                                    return (
                                                                        <div key={pi} style={{ marginBottom: pi < procs.length - 1 ? 4 : 0 }}>
                                                                            {pi > 0 && <div style={{ borderTop: `1px solid ${T.border}22`, marginBottom: 4 }} />}
                                                                            <ProcedureTreeNode
                                                                                node={{ ...(proc.json_layers || { children: [], features: [] }), name: proc.procedure_name }}
                                                                                vis={vis}
                                                                                onChange={handleChange}
                                                                                depth={0}
                                                                            />
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    );
                                })()}
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* ── Right: map + results table ── */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative' }}>
                {/* Map controls — Street/Satellite only */}
                <div style={{ position: 'absolute', top: 12, right: 12, zIndex: 1000, display: 'flex', gap: 6, background: 'rgba(6,14,26,0.85)', borderRadius: 8, padding: 6, border: `1px solid ${T.border}`, backdropFilter: 'blur(8px)' }}>
                    {Object.entries(TILE_LAYERS).map(([key, layer]) => (
                        <button key={key} onClick={() => setMapMode(key)} style={{ padding: '4px 10px', borderRadius: 5, border: 'none', cursor: 'pointer', background: mapMode === key ? T.blue : 'transparent', color: mapMode === key ? T.white : T.muted, fontSize: 11, fontWeight: mapMode === key ? 700 : 400 }}>{layer.label}</button>
                    ))}
                </div>

                {/* Legend */}
                <div style={{ position: 'absolute', top: 12, left: 12, zIndex: 1000, background: 'rgba(6,14,26,0.85)', borderRadius: 8, padding: '6px 10px', border: `1px solid ${T.border}`, backdropFilter: 'blur(8px)', fontSize: 10, color: T.muted }}>
                    <div style={{ fontWeight: 700, color: T.white, marginBottom: 4 }}>NPA Areas</div>
                    {assessedIcaos.map(icao => (
                        <div key={icao} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                            <div style={{ width: 10, height: 4, borderRadius: 2, background: getRectColor(icao, assessedIcaos), opacity: 0.8 }} />
                            <span>{icao}</span>
                        </div>
                    ))}
                    {assessedIcaos.length === 0 && <span style={{ fontStyle: 'italic' }}>No areas loaded</span>}
                    <div style={{ marginTop: 4, borderTop: `1px solid ${T.border}33`, paddingTop: 4 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}><div style={{ width: 10, height: 10, borderRadius: 50, background: PENETRATE_COLOR }} />Penetrates OCA</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}><div style={{ width: 10, height: 10, borderRadius: 50, background: CLEAR_COLOR }} />Within area / clear</div>
                    </div>
                </div>

                <ShieldControl top={92} shieldOn={shieldOn} toggleShield={toggleShield} shieldData={shieldData} shieldLoading={shieldLoading} shieldError={shieldError} projectMaxTotalElev={projectMaxTotalElev} projectPenetratingMaxTotalElev={projectPenetratingMaxTotalElev} radiusKm={radiusKm} setRadiusKm={setRadiusKm} exportCsv={exportCsv}
                    penetratingCount={missedApproachRealPenetration.size}
                    onExportPenetrating={() => {
                        const byId = new Map();
                        [...(shieldData?.swot || []).map(r => ({ ...r, kind: 'SWOT' })),
                         ...(shieldData?.sof || []).map(r => ({ ...r, kind: 'SOF' }))].forEach(r => {
                            byId.set(`shield_${r.kind}_${r.ogc_fid}`, r);
                        });
                        const entries = [];
                        missedApproachRealPenetration.forEach((penList, obsId) => {
                            const r = byId.get(obsId);
                            if (!r) return;
                            penList.forEach(v => entries.push({
                                r,
                                surfaceLabel: `Missed Approach ${v.icao} RWY ${v.runway}`,
                                penetrationAmount: (-v.margin).toFixed(2),
                            }));
                        });
                        exportSurfacePenetratingCsv(entries, radiusKm);
                    }} />
                {shieldOn && hasMissedApproachImpact && (
                    <div style={{ position: 'absolute', top: 226, right: 12, zIndex: 1000, background: 'rgba(6,14,26,0.85)', borderRadius: 8, padding: 8, border: '1px solid #FFB02055', backdropFilter: 'blur(8px)', maxWidth: 260, fontSize: 10, color: T.muted, lineHeight: 1.5 }}>
                        <div style={{ color: '#FFB020', fontWeight: 700, marginBottom: 2 }}>🔄 Missed Approach (real check)</div>
                        {missedApproachShieldVerdicts.size > 0
                            ? <span><b style={{ color: '#FFB020' }}>{missedApproachShieldVerdicts.size}</b> obstacle(s) confirmed to already penetrate the Missed Approach segment as much or more than this project's worst case — not a new impact.</span>
                            : 'No candidate already penetrates the Missed Approach segment at least as much as this project — flat elevation comparison alone would be misleading here.'}
                    </div>
                )}

                <MapMeasureTool map={mapState} top={52} tableOffset={resultsCollapsed ? 40 : (activeIcao ? 280 : 0)} />
                <MapCoordinateViewer map={mapState} />
                <div ref={mapRef} style={{ flex: 1 }} />

                {/* Results table */}
                <div style={{ height: activeIcao ? (resultsCollapsed ? 38 : resultsHeight) : 0, background: T.bg1, borderTop: activeIcao ? `1px solid ${T.border}` : 'none', display: 'flex', flexDirection: 'column', transition: 'height 0.2s ease', overflow: 'hidden', flexShrink: 0 }}>
                        {!resultsCollapsed && <ResizeHandle onMouseDown={startResultsResize} />}
                    <div style={{ padding: '8px 20px', background: T.bg0, borderBottom: `1px solid ${T.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: T.white, display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span>🏔</span> NPA Results
                        </div>
                        {activeIcao && (
                            <div style={{ display: 'flex', gap: 12 }}>
                                {!resultsPoppedOut && (
                                    <button
                                        onClick={() => {
                                            setResultsPoppedOut(true);
                                            setResultsCollapsed(true);
                                        }}
                                        style={{
                                            background: 'none', border: 'none', color: T.blueLt, cursor: 'pointer', fontSize: 11,
                                            display: 'flex', alignItems: 'center', gap: 4, fontWeight: 600
                                        }}
                                        title="Pop out results to a movable window"
                                    >
                                        🗖 Pop Out
                                    </button>
                                )}
                                <button
                                    onClick={() => setResultsCollapsed(!resultsCollapsed)}
                                    style={{
                                        background: 'none', border: 'none', color: T.blueLt, cursor: 'pointer', fontSize: 11,
                                        fontWeight: 600
                                    }}
                                >
                                    {resultsCollapsed ? '▲ Expand' : '▼ Collapse'}
                                </button>
                            </div>
                        )}
                    </div>
                    <div style={{ flex: 1, overflow: 'hidden' }}>
                        {resultsPoppedOut ? (
                            <div style={{ padding: 20, textAlign: 'center', color: T.muted, fontSize: 13 }}>
                                Results are popped out. 
                                <button 
                                    onClick={() => { setResultsPoppedOut(false); setResultsCollapsed(false); }} 
                                    style={{ background: 'none', border: 'none', color: T.blue, textDecoration: 'underline', cursor: 'pointer', marginLeft: 6, fontWeight: 600 }}
                                >
                                    Bring back
                                </button>
                            </div>
                        ) : renderResultsContent()}
                    </div>
                </div>
            </div>

            {resultsPoppedOut && (
                <DraggableModal
                    title={`${project?.name || ''} - NPA Results`}
                    onClose={() => setResultsPoppedOut(false)}
                    width={850}
                    height={500}
                >
                    {renderResultsContent()}
                </DraggableModal>
            )}
        </div>
    );
}
